import {
  useFetchComicNameAutocomplete,
  useFetchImageAIRecommendation,
  useFetchAdminImageData,
  useFetchAdminImageDetail,
  useFetchAdminLastIds,
  useFetchMetaTagAutocomplete,
  usePatchAdminImageData,
  usePatchIssueReportList,
  usePostImagesBulkDelete,
  usePostAdminImageList,
  usePostAdminPresignedUrl
} from '@api';
import {
  FemaleBodyChildOptionValueEnum,
  HorizontalAngleChildOptionValueEnum,
  AdminModalType,
  AdultsOnlyShotChildOptionValueEnum,
  PathnameEnum,
  SexualBodyPartChildOptionValueEnum,
  SexualPositionChildOptionValueEnum,
  VerticalAngleChildOptionValueEnum,
  optionsConstant,
  CategorySelectEnum,
  QueryParamKeyEnum,
  FromPageQueryParamValueEnum,
  AgeRangeChildOptionValueEnum,
  MainMotionPartChildOptionValueEnum,
  StandardFacialExpressionChildOptionValueEnum,
  PostureCategoryChildOptionValueEnum,
  StandardShotChildOptionValueEnum,
  ToastMessageEnum
} from '@constant';
import { SelectChangeEvent } from '@mui/material';
import {
  IChildOptionForPostImageListItem,
  IChildOptions,
  IComicName,
  IIssueReportListItem,
  IOptionList,
  IPatchImageUploadBody,
  IPostImageListItem,
  IUpdates,
  IUploadImageListItem
} from '@types';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { IAddImages } from '@types';
import { useToast } from './useToast';

//어드민 - 이미지 업로드 및 이미지 정보 수정 페이지 로직에 관여
export const useAdminImageUpsert = () => {
  const { imageIdPathParam } = useParams<{ imageIdPathParam: string }>();
  const imageId = imageIdPathParam ? Number(imageIdPathParam) : undefined;
  const [searchParams] = useSearchParams();
  const sequenceId = searchParams.get('sequenceId') ? Number(searchParams.get('sequenceId')) : undefined;

  // 브레드크럼: 이전 페이지 정보를 쿼리 파라미터에서 가져오기
  const fromPage = searchParams.get(QueryParamKeyEnum.FROM_PAGE) || FromPageQueryParamValueEnum.STANDARD;
  const fromTheme = (searchParams.get(QueryParamKeyEnum.THEME) as CategorySelectEnum) || CategorySelectEnum.STANDARD;

  const isCreate = Boolean(!imageId);

  //저장 버튼 disabled 처리 관련, 둘 중 하나라도 true라면 disabled={true}
  const [needImageAttach, setNeedImageAttach] = useState<boolean>(true);
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false);
  //분류 Select 관련
  const [category, setCategory] = useState<CategorySelectEnum>(fromTheme);
  const [prevCategory, setPrevCategory] = useState<CategorySelectEnum>(fromTheme);
  //체위 Select 관련
  const [originalSelectedSexualPosition, setOriginalSelectedSexualPosition] = useState(
    SexualPositionChildOptionValueEnum.정상위
  );
  const [selectedSexualPosition, setSelectedSexualPosition] = useState(SexualPositionChildOptionValueEnum.정상위);
  //작품명 관련
  const [originalComic, setOriginalComic] = useState<IComicName>({ id: undefined, name: '' });
  const [autocompleteKeyword, setAutocompleteKeyword] = useState<string>('');
  const [inputKeyword, setInputKeyword] = useState<string>('');
  //이미지 리스트
  const [originalImageList, setOriginalImageList] = useState<IUploadImageListItem[]>([]);
  const [imageList, setImageList] = useState<IUploadImageListItem[]>([]);
  const [focusedImageIndex, setFocusedImageIndex] = useState<number | null>(null);
  const [prevImageListLength, setPrevImageListLength] = useState<number>(0); // 이미지 업로드 페이지 첫 이미지 업로드시 0번 자동 focus을 위한 state
  //태그 관련
  const [tagAutocompleteKeyword, setTagAutocompleteKeyword] = useState<string>('');
  const [tagInputKeyword, setTagInputKeyword] = useState<string>('');
  const [isTagging, setIsTagging] = useState(false); // 스페이스 입력 감지 상태
  //모달 관련
  const [modalType, setModalType] = useState<AdminModalType>(AdminModalType.IMAGE_UPLOAD_CANCEL);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalContent, setModalContent] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  //저장버튼 클릭시 error state 관련
  const [isSaveAttempted, setIsSaveAttempted] = useState(false);

  const navigate = useNavigate();

  const { showToast } = useToast();

  const { data: lastIdsData } = useFetchAdminLastIds();
  const lastImageId = lastIdsData ? lastIdsData.lastImageId : 1;
  const lastSequenceId = lastIdsData ? lastIdsData.lastSequenceId : 1;

  const { data: comicNameAutocompleteData } = useFetchComicNameAutocomplete(
    autocompleteKeyword && autocompleteKeyword.length > 0 ? autocompleteKeyword : null
  );
  const autocompleteList =
    autocompleteKeyword &&
    autocompleteKeyword.length > 0 &&
    comicNameAutocompleteData &&
    comicNameAutocompleteData.length > 0
      ? comicNameAutocompleteData.map((item) => item.name)
      : [];

  const { data: metaTagAutocompleteData } = useFetchMetaTagAutocomplete(
    tagAutocompleteKeyword && tagAutocompleteKeyword.length > 0 ? tagAutocompleteKeyword : null
  );
  const tagAutocompleteList =
    tagAutocompleteKeyword &&
    tagAutocompleteKeyword.length > 0 &&
    metaTagAutocompleteData &&
    metaTagAutocompleteData.length > 0
      ? metaTagAutocompleteData.map((item) => item.name)
      : [];

  const { mutateAsync: postPresignedUrlMutateAsync } = usePostAdminPresignedUrl();

  const { mutateAsync: fetchImageAIRecommendationMutateAsync, isPending: isFetchImageAIRecommendationLoading } =
    useFetchImageAIRecommendation();

  const { mutate: postImageListMutate } = usePostAdminImageList(
    () => {
      setModalType(AdminModalType.IMAGE_UPLOAD_SUCCESS);
      setModalTitle('업로드에 성공했습니다.');
      setModalContent('리스트로 돌아갑니다.');
      setIsModalVisible(true);
    },
    () => {
      setModalType(AdminModalType.IMAGE_UPLOAD_REQUEST_FAIL);
      setModalTitle('업로드에 실패하였습니다.');
      setModalContent('요청이 실패하였습니다. 다시 시도해주세요.');
      setIsModalVisible(true);
      setIsRequestPending(false); //저장버튼 활성화
    }
  );

  const { data: fetchedImageData } = useFetchAdminImageData(imageId, sequenceId);

  const { mutateAsync: fetchImageDetailMutateAsync } = useFetchAdminImageDetail();

  const { mutateAsync: patchImageDataMutateAsync } = usePatchAdminImageData();

  const { mutateAsync: patchIssueReportListMutateAsync } = usePatchIssueReportList();

  const { mutateAsync: postImagesBulkDeleteMutateAsync } = usePostImagesBulkDelete();

  // 현재 focus된 이미지 반환
  const focusedImage = focusedImageIndex !== null ? imageList[focusedImageIndex] : null;

  // 브레드크럼 텍스트와 이동 경로를 결정하는 함수
  const getBreadcrumbInfo = () => {
    if (fromPage === FromPageQueryParamValueEnum.STANDARD) {
      return {
        text: '스탠다드 검색 이미지 리스트',
        path: PathnameEnum.ADMIN_STANDARD_IMAGE_LIST_PAGE
      };
    } else if (fromPage === FromPageQueryParamValueEnum.ADULTS_ONLY) {
      return {
        text: '19금 이미지 리스트',
        path: PathnameEnum.ADMIN_ADULTS_ONLY_IMAGE_LIST_PAGE
      };
    } else if (fromPage === FromPageQueryParamValueEnum.OPEN_ISSUE_REPORT_LIST) {
      return {
        text: '접수된 오류',
        path: PathnameEnum.ADMIN_OPEN_ISSUE_REPORT_LIST
      };
    } else if (fromPage === FromPageQueryParamValueEnum.RESOLVED_ISSUE_REPORT_LIST) {
      return {
        text: '처리 완료',
        path: PathnameEnum.ADMIN_RESOLVED_ISSUE_REPORT_LIST
      };
    } else if (fromPage === FromPageQueryParamValueEnum.DELETED_IMAGE_LIST) {
      return {
        text: '삭제된 이미지 리스트',
        path: PathnameEnum.ADMIN_DELETED_IMAGE_LIST_PAGE
      };
    } else {
      // 기본값 (이전 페이지 정보가 없는 경우)
      return {
        text: '스탠다드 검색 이미지 리스트',
        path: PathnameEnum.ADMIN_STANDARD_IMAGE_LIST_PAGE
      };
    }
  };

  const handleBreadCrumbClick = () => {
    const { path } = getBreadcrumbInfo();
    navigate(path);
  };

  const handlePageCancelClick = () => {
    if (isCreate) {
      setModalType(AdminModalType.IMAGE_UPLOAD_CANCEL);
      setModalTitle('이미지 업로드를 중단하시겠습니까?');
    } else {
      setModalType(AdminModalType.IMAGE_EDIT_CANCEL);
      setModalTitle('이미지 수정을 중단하시겠습니까?');
    }
    setModalContent('이 작업은 되돌릴 수 없습니다.');
    setIsModalVisible(true);
  };

  const handleCategoryChange = (event: SelectChangeEvent<unknown>) => {
    const newCategory = event.target.value as CategorySelectEnum;
    setCategory(newCategory);

    if (imageList.length > 0) {
      // 카테고리 변경 시 imageList의 childOptions 업데이트
      setImageList((prevImageList) =>
        prevImageList.map((image) => ({
          ...image,
          childOptions: newCategory === CategorySelectEnum.STANDARD ? null : { sexualPosition: selectedSexualPosition },
          tags: [],
          aiRecommendedChildOptions: undefined,
          aiRecommendedTags: undefined
        }))
      );
    }
  };

  // 특정 이미지에 focus를 주는 함수
  const handleImageFocus = (targetImageIndex: number) => {
    //우측 이미지 정보란에 focusedImage의 정보를 보여주기 위함
    setFocusedImageIndex(targetImageIndex);

    //이미지 리스트의 이미지 타일에 focus 효과 주기 위해 isFocus: true setState
    const newImageList = imageList.map((item, idx) => ({
      ...item,
      isFocus: idx === targetImageIndex
    }));

    setImageList(newImageList);

    const targetImageParam = newImageList[targetImageIndex];

    invokeImageApiCall(targetImageParam, targetImageIndex);
  };

  // 업로드/정보수정 과정 중 단일 이미지 관련 API 호출을 촉발하는 함수
  const invokeImageApiCall = (targetImage: IUploadImageListItem, targetImageIndex: number) => {
    // 새로 업로드된 이미지의 경우 POST /images/presigned-url 요청을 invoke
    if (!targetImage?.imageId && !targetImage?.key && !targetImage?.presignedUrl) {
      return sendPostPresignedUrl(targetImage, targetImageIndex);
    }

    // 기존에 업로드된 이미지의 경우 focusedImage의 정보를 불러오기
    if (targetImage?.imageId && (!targetImage?.childOptions || !targetImage?.aiRecommendedChildOptions)) {
      return getImageDetail(targetImage, targetImageIndex);
    }

    // 새로 업로드된 이미지에 대해 S3 upload까지 완료 -> 분류값 변경시 GET /images/tag-suggestions 요청을 invoke.
    if (targetImage.key && targetImage.presignedUrl && !targetImage.aiRecommendedChildOptions) {
      return getAIRecommendation(targetImage, targetImageIndex, category);
    }
  };

  // GET /images/:id/issue-reports 데이터 setState
  const getImageIssueReportList = (imageId: number, issueReportList: IIssueReportListItem[]) => {
    if (focusedImage && !focusedImage.issueReportList) {
      setImageList((prev) => {
        // 기존 리스트 복사
        const newList = prev.map((item, idx) => {
          if (item.imageId !== imageId) return item;
          // childOptions도 새 객체로 복사
          return {
            ...item,
            issueReportList: [...issueReportList],
            checkedIssueReportIds: []
          };
        });
        return newList;
      });
    }
  };

  // 체크한 오류 리포트 setState
  const updateCheckedIssueReportIds = (imageId: number, checkedIssueReportId: number) => {
    setImageList((prevImageList) =>
      prevImageList.map((image) => {
        if (image.imageId === imageId) {
          const currentIds = image.checkedIssueReportIds || [];
          const isChecked = currentIds.includes(checkedIssueReportId);

          if (isChecked) {
            // 이미 체크된 경우: 배열에서 제거
            return {
              ...image,
              checkedIssueReportIds: currentIds.filter((id) => id !== checkedIssueReportId)
            };
          } else {
            // 체크되지 않은 경우: 배열에 추가
            return {
              ...image,
              checkedIssueReportIds: [...currentIds, checkedIssueReportId]
            };
          }
        }
        return image;
      })
    );
  };

  // GET /admin/images/:imageId/detail 요청 전송 및 GET /images/tag-suggestions 요청을 invoke
  const getImageDetail = async (targetImage: IUploadImageListItem, targetImageIndex: number) => {
    // 기존에 업로드된 이미지 중 아직 child option값을 불러오지 않는 이미지에 한해 요청 전송
    if (targetImage?.imageId && !targetImage.childOptions) {
      await fetchImageDetailMutateAsync(targetImage?.imageId).then((res) => {
        const fetchedImageDetailData = res.data.data;

        if (category === CategorySelectEnum.STANDARD) {
          setImageList((prev) => {
            // 기존 리스트 복사
            const newList = prev.map((item) => {
              if (item.imageId !== targetImage.imageId) return item;

              const { image, imageOptionValue, metaTag } = fetchedImageDetailData;

              // 옵션 및 태그값 업데이트 여부 확인을 위해 original 프로퍼티를 함께 생성
              return {
                ...item,
                isReported: image.isReported,
                originalChildOptions: {
                  backgroundCut: Object.prototype.hasOwnProperty.call(imageOptionValue, '배경컷')
                    ? {
                        해당: { name: '해당', value: 'true', isSelected: imageOptionValue['배경컷'] as boolean },
                        미해당: { name: '미해당', value: 'false', isSelected: !imageOptionValue['배경컷'] as boolean }
                      }
                    : null,
                  characterNumber:
                    imageOptionValue['남성 인원수'] != null &&
                    imageOptionValue['여성 인원수'] != null &&
                    !(imageOptionValue['남성 인원수'] === 0 && imageOptionValue['여성 인원수'] === 0)
                      ? {
                          maleNumber: imageOptionValue['남성 인원수'],
                          femaleNumber: imageOptionValue['여성 인원수']
                        }
                      : null,
                  shot:
                    imageOptionValue['샷 구분'] && imageOptionValue['샷 구분'].length > 0
                      ? {
                          풀샷: {
                            name: '풀샷',
                            value: StandardShotChildOptionValueEnum['풀샷'],
                            isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['풀샷']
                          },
                          미디엄샷: {
                            name: '미디엄샷',
                            value: StandardShotChildOptionValueEnum['미디엄샷'],
                            isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['미디엄샷']
                          },
                          클로즈업: {
                            name: '클로즈업',
                            value: StandardShotChildOptionValueEnum['클로즈업'],
                            isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['클로즈업']
                          },
                          롱샷: {
                            name: '롱샷',
                            value: StandardShotChildOptionValueEnum['롱샷'],
                            isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['롱샷']
                          }
                        }
                      : null,
                  directed: Object.prototype.hasOwnProperty.call(imageOptionValue, '연출')
                    ? {
                        해당: { name: '해당', value: 'true', isSelected: imageOptionValue['연출'] as boolean },
                        미해당: { name: '미해당', value: 'false', isSelected: !imageOptionValue['연출'] as boolean }
                      }
                    : null,
                  verticalAngle:
                    imageOptionValue['세로앵글'] && imageOptionValue['세로앵글'].length > 0
                      ? {
                          로우앵글: {
                            name: '로우앵글',
                            value: VerticalAngleChildOptionValueEnum['로우앵글'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['로우앵글']
                          },
                          하이앵글: {
                            name: '하이앵글',
                            value: VerticalAngleChildOptionValueEnum['하이앵글'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['하이앵글']
                          },
                          항공샷: {
                            name: '항공샷',
                            value: VerticalAngleChildOptionValueEnum['항공샷'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['항공샷']
                          },
                          평시각: {
                            name: '평시각',
                            value: VerticalAngleChildOptionValueEnum['평시각'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['평시각']
                          }
                        }
                      : null,
                  ageRange:
                    imageOptionValue.연령대 && imageOptionValue.연령대.length > 0
                      ? {
                          유아: {
                            name: '유아',
                            value: AgeRangeChildOptionValueEnum['유아'],
                            isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['유아'])
                          },
                          청소년: {
                            name: '청소년',
                            value: AgeRangeChildOptionValueEnum['청소년'],
                            isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['청소년'])
                          },
                          청년: {
                            name: '청년',
                            value: AgeRangeChildOptionValueEnum['청년'],
                            isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['청년'])
                          },
                          중장년: {
                            name: '중장년',
                            value: AgeRangeChildOptionValueEnum['중장년'],
                            isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['중장년'])
                          },
                          노인: {
                            name: '노인',
                            value: AgeRangeChildOptionValueEnum['노인'],
                            isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['노인'])
                          }
                        }
                      : null,
                  mainMotionPart:
                    imageOptionValue.중심동작부위 && imageOptionValue.중심동작부위.length > 0
                      ? {
                          손: {
                            name: '손',
                            value: MainMotionPartChildOptionValueEnum['손'],
                            isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['손'])
                          },
                          팔: {
                            name: '팔',
                            value: MainMotionPartChildOptionValueEnum['팔'],
                            isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['팔'])
                          },
                          다리: {
                            name: '다리',
                            value: MainMotionPartChildOptionValueEnum['다리'],
                            isSelected: imageOptionValue.중심동작부위.includes(
                              MainMotionPartChildOptionValueEnum['다리']
                            )
                          },
                          발: {
                            name: '발',
                            value: MainMotionPartChildOptionValueEnum['발'],
                            isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['발'])
                          },
                          머리: {
                            name: '머리',
                            value: MainMotionPartChildOptionValueEnum['머리'],
                            isSelected: imageOptionValue.중심동작부위.includes(
                              MainMotionPartChildOptionValueEnum['머리']
                            )
                          },
                          전신: {
                            name: '전신',
                            value: MainMotionPartChildOptionValueEnum['전신'],
                            isSelected: imageOptionValue.중심동작부위.includes(
                              MainMotionPartChildOptionValueEnum['전신']
                            )
                          },
                          상체: {
                            name: '상체',
                            value: MainMotionPartChildOptionValueEnum['상체'],
                            isSelected: imageOptionValue.중심동작부위.includes(
                              MainMotionPartChildOptionValueEnum['상체']
                            )
                          },
                          하체: {
                            name: '하체',
                            value: MainMotionPartChildOptionValueEnum['하체'],
                            isSelected: imageOptionValue.중심동작부위.includes(
                              MainMotionPartChildOptionValueEnum['하체']
                            )
                          }
                        }
                      : null,
                  facialExpression:
                    imageOptionValue.표정 && imageOptionValue.표정.length > 0
                      ? {
                          없음: {
                            name: '없음',
                            value: StandardFacialExpressionChildOptionValueEnum['없음'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['없음']
                            )
                          },
                          만족: {
                            name: '만족',
                            value: StandardFacialExpressionChildOptionValueEnum['만족'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['만족']
                            )
                          },
                          미소: {
                            name: '미소',
                            value: StandardFacialExpressionChildOptionValueEnum['미소'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['미소']
                            )
                          },
                          새침: {
                            name: '새침',
                            value: StandardFacialExpressionChildOptionValueEnum['새침'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['새침']
                            )
                          },
                          기쁨: {
                            name: '기쁨',
                            value: StandardFacialExpressionChildOptionValueEnum['기쁨'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['기쁨']
                            )
                          },
                          폭소: {
                            name: '폭소',
                            value: StandardFacialExpressionChildOptionValueEnum['폭소'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['폭소']
                            )
                          },
                          수줍음: {
                            name: '수줍음',
                            value: StandardFacialExpressionChildOptionValueEnum['수줍음'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['수줍음']
                            )
                          },
                          무표정: {
                            name: '무표정',
                            value: StandardFacialExpressionChildOptionValueEnum['무표정'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['무표정']
                            )
                          },
                          놀람: {
                            name: '놀람',
                            value: StandardFacialExpressionChildOptionValueEnum['놀람'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['놀람']
                            )
                          },
                          고민: {
                            name: '고민',
                            value: StandardFacialExpressionChildOptionValueEnum['고민'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['고민']
                            )
                          },
                          진지: {
                            name: '진지',
                            value: StandardFacialExpressionChildOptionValueEnum['진지'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['진지']
                            )
                          },
                          혐오: {
                            name: '혐오',
                            value: StandardFacialExpressionChildOptionValueEnum['혐오'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['혐오']
                            )
                          },
                          분노: {
                            name: '분노',
                            value: StandardFacialExpressionChildOptionValueEnum['분노'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['분노']
                            )
                          },
                          당황: {
                            name: '당황',
                            value: StandardFacialExpressionChildOptionValueEnum['당황'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['당황']
                            )
                          },
                          공포: {
                            name: '공포',
                            value: StandardFacialExpressionChildOptionValueEnum['공포'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['공포']
                            )
                          },
                          슬픔: {
                            name: '슬픔',
                            value: StandardFacialExpressionChildOptionValueEnum['슬픔'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['슬픔']
                            )
                          },
                          경계: {
                            name: '경계',
                            value: StandardFacialExpressionChildOptionValueEnum['경계'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['경계']
                            )
                          },
                          좌절: {
                            name: '좌절',
                            value: StandardFacialExpressionChildOptionValueEnum['좌절'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['좌절']
                            )
                          },
                          뻔뻔: {
                            name: '뻔뻔',
                            value: StandardFacialExpressionChildOptionValueEnum['뻔뻔'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['뻔뻔']
                            )
                          },
                          수치: {
                            name: '수치',
                            value: StandardFacialExpressionChildOptionValueEnum['수치'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['수치']
                            )
                          },
                          의심: {
                            name: '의심',
                            value: StandardFacialExpressionChildOptionValueEnum['의심'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['의심']
                            )
                          },
                          불안: {
                            name: '불안',
                            value: StandardFacialExpressionChildOptionValueEnum['불안'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['불안']
                            )
                          },
                          절규: {
                            name: '절규',
                            value: StandardFacialExpressionChildOptionValueEnum['절규'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['절규']
                            )
                          }
                        }
                      : null,
                  deforme: Object.prototype.hasOwnProperty.call(imageOptionValue, '데포르메 여부')
                    ? {
                        해당: {
                          name: '해당',
                          value: 'true',
                          isSelected: imageOptionValue['데포르메 여부'] as boolean
                        },
                        미해당: {
                          name: '미해당',
                          value: 'false',
                          isSelected: !imageOptionValue['데포르메 여부'] as boolean
                        }
                      }
                    : null,
                  horizontalAngle:
                    imageOptionValue['가로앵글'] && imageOptionValue['가로앵글'].length > 0
                      ? {
                          정면: {
                            name: '정면',
                            value: HorizontalAngleChildOptionValueEnum['정면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['정면']
                            )
                          },
                          정측면: {
                            name: '정측면',
                            value: HorizontalAngleChildOptionValueEnum['정측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['정측면']
                            )
                          },
                          측면: {
                            name: '측면',
                            value: HorizontalAngleChildOptionValueEnum['측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['측면']
                            )
                          },
                          후측면: {
                            name: '후측면',
                            value: HorizontalAngleChildOptionValueEnum['후측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['후측면']
                            )
                          },
                          후면: {
                            name: '후면',
                            value: HorizontalAngleChildOptionValueEnum['후면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['후면']
                            )
                          }
                        }
                      : null,
                  postureCategory:
                    imageOptionValue['동작구분'] && imageOptionValue['동작구분'].length > 0
                      ? {
                          없음: {
                            name: '없음',
                            value: PostureCategoryChildOptionValueEnum['없음'],
                            isSelected: imageOptionValue['동작구분'].includes(
                              PostureCategoryChildOptionValueEnum['없음']
                            )
                          },
                          서있음: {
                            name: '서있음',
                            value: PostureCategoryChildOptionValueEnum['서있음'],
                            isSelected: imageOptionValue['동작구분'].includes(
                              PostureCategoryChildOptionValueEnum['서있음']
                            )
                          },
                          앉음: {
                            name: '앉음',
                            value: PostureCategoryChildOptionValueEnum['앉음'],
                            isSelected: imageOptionValue['동작구분'].includes(
                              PostureCategoryChildOptionValueEnum['앉음']
                            )
                          },
                          누움: {
                            name: '누움',
                            value: PostureCategoryChildOptionValueEnum['누움'],
                            isSelected: imageOptionValue['동작구분'].includes(
                              PostureCategoryChildOptionValueEnum['누움']
                            )
                          },
                          엎드림: {
                            name: '엎드림',
                            value: PostureCategoryChildOptionValueEnum['엎드림'],
                            isSelected: imageOptionValue['동작구분'].includes(
                              PostureCategoryChildOptionValueEnum['엎드림']
                            )
                          }
                        }
                      : null,
                  skinship: Object.prototype.hasOwnProperty.call(imageOptionValue, '스킨십')
                    ? {
                        해당: {
                          name: '해당',
                          value: 'true',
                          isSelected: imageOptionValue['스킨십'] as boolean
                        },
                        미해당: {
                          name: '미해당',
                          value: 'false',
                          isSelected: !imageOptionValue['스킨십'] as boolean
                        }
                      }
                    : null
                },
                childOptions: {
                  backgroundCut: Object.prototype.hasOwnProperty.call(imageOptionValue, '배경컷')
                    ? {
                        해당: { name: '해당', value: 'true', isSelected: imageOptionValue['배경컷'] as boolean },
                        미해당: { name: '미해당', value: 'false', isSelected: !imageOptionValue['배경컷'] as boolean }
                      }
                    : null,
                  characterNumber:
                    imageOptionValue['남성 인원수'] != null &&
                    imageOptionValue['여성 인원수'] != null &&
                    !(imageOptionValue['남성 인원수'] === 0 && imageOptionValue['여성 인원수'] === 0)
                      ? {
                          maleNumber: imageOptionValue['남성 인원수'],
                          femaleNumber: imageOptionValue['여성 인원수']
                        }
                      : null,
                  shot:
                    imageOptionValue['샷 구분'] && imageOptionValue['샷 구분'].length > 0
                      ? {
                          풀샷: {
                            name: '풀샷',
                            value: StandardShotChildOptionValueEnum['풀샷'],
                            isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['풀샷']
                          },
                          미디엄샷: {
                            name: '미디엄샷',
                            value: StandardShotChildOptionValueEnum['미디엄샷'],
                            isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['미디엄샷']
                          },
                          클로즈업: {
                            name: '클로즈업',
                            value: StandardShotChildOptionValueEnum['클로즈업'],
                            isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['클로즈업']
                          },
                          롱샷: {
                            name: '롱샷',
                            value: StandardShotChildOptionValueEnum['롱샷'],
                            isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['롱샷']
                          }
                        }
                      : null,
                  directed: Object.prototype.hasOwnProperty.call(imageOptionValue, '연출')
                    ? {
                        해당: { name: '해당', value: 'true', isSelected: imageOptionValue['연출'] as boolean },
                        미해당: { name: '미해당', value: 'false', isSelected: !imageOptionValue['연출'] as boolean }
                      }
                    : null,
                  verticalAngle:
                    imageOptionValue['세로앵글'] && imageOptionValue['세로앵글'].length > 0
                      ? {
                          로우앵글: {
                            name: '로우앵글',
                            value: VerticalAngleChildOptionValueEnum['로우앵글'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['로우앵글']
                          },
                          하이앵글: {
                            name: '하이앵글',
                            value: VerticalAngleChildOptionValueEnum['하이앵글'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['하이앵글']
                          },
                          항공샷: {
                            name: '항공샷',
                            value: VerticalAngleChildOptionValueEnum['항공샷'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['항공샷']
                          },
                          평시각: {
                            name: '평시각',
                            value: VerticalAngleChildOptionValueEnum['평시각'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['평시각']
                          }
                        }
                      : null,
                  ageRange:
                    imageOptionValue.연령대 && imageOptionValue.연령대.length > 0
                      ? {
                          유아: {
                            name: '유아',
                            value: AgeRangeChildOptionValueEnum['유아'],
                            isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['유아'])
                          },
                          청소년: {
                            name: '청소년',
                            value: AgeRangeChildOptionValueEnum['청소년'],
                            isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['청소년'])
                          },
                          청년: {
                            name: '청년',
                            value: AgeRangeChildOptionValueEnum['청년'],
                            isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['청년'])
                          },
                          중장년: {
                            name: '중장년',
                            value: AgeRangeChildOptionValueEnum['중장년'],
                            isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['중장년'])
                          },
                          노인: {
                            name: '노인',
                            value: AgeRangeChildOptionValueEnum['노인'],
                            isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['노인'])
                          }
                        }
                      : null,
                  mainMotionPart:
                    imageOptionValue.중심동작부위 && imageOptionValue.중심동작부위.length > 0
                      ? {
                          손: {
                            name: '손',
                            value: MainMotionPartChildOptionValueEnum['손'],
                            isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['손'])
                          },
                          팔: {
                            name: '팔',
                            value: MainMotionPartChildOptionValueEnum['팔'],
                            isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['팔'])
                          },
                          다리: {
                            name: '다리',
                            value: MainMotionPartChildOptionValueEnum['다리'],
                            isSelected: imageOptionValue.중심동작부위.includes(
                              MainMotionPartChildOptionValueEnum['다리']
                            )
                          },
                          발: {
                            name: '발',
                            value: MainMotionPartChildOptionValueEnum['발'],
                            isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['발'])
                          },
                          머리: {
                            name: '머리',
                            value: MainMotionPartChildOptionValueEnum['머리'],
                            isSelected: imageOptionValue.중심동작부위.includes(
                              MainMotionPartChildOptionValueEnum['머리']
                            )
                          },
                          전신: {
                            name: '전신',
                            value: MainMotionPartChildOptionValueEnum['전신'],
                            isSelected: imageOptionValue.중심동작부위.includes(
                              MainMotionPartChildOptionValueEnum['전신']
                            )
                          },
                          상체: {
                            name: '상체',
                            value: MainMotionPartChildOptionValueEnum['상체'],
                            isSelected: imageOptionValue.중심동작부위.includes(
                              MainMotionPartChildOptionValueEnum['상체']
                            )
                          },
                          하체: {
                            name: '하체',
                            value: MainMotionPartChildOptionValueEnum['하체'],
                            isSelected: imageOptionValue.중심동작부위.includes(
                              MainMotionPartChildOptionValueEnum['하체']
                            )
                          }
                        }
                      : null,
                  facialExpression:
                    imageOptionValue.표정 && imageOptionValue.표정.length > 0
                      ? {
                          없음: {
                            name: '없음',
                            value: StandardFacialExpressionChildOptionValueEnum['없음'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['없음']
                            )
                          },
                          만족: {
                            name: '만족',
                            value: StandardFacialExpressionChildOptionValueEnum['만족'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['만족']
                            )
                          },
                          미소: {
                            name: '미소',
                            value: StandardFacialExpressionChildOptionValueEnum['미소'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['미소']
                            )
                          },
                          새침: {
                            name: '새침',
                            value: StandardFacialExpressionChildOptionValueEnum['새침'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['새침']
                            )
                          },
                          기쁨: {
                            name: '기쁨',
                            value: StandardFacialExpressionChildOptionValueEnum['기쁨'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['기쁨']
                            )
                          },
                          폭소: {
                            name: '폭소',
                            value: StandardFacialExpressionChildOptionValueEnum['폭소'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['폭소']
                            )
                          },
                          수줍음: {
                            name: '수줍음',
                            value: StandardFacialExpressionChildOptionValueEnum['수줍음'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['수줍음']
                            )
                          },
                          무표정: {
                            name: '무표정',
                            value: StandardFacialExpressionChildOptionValueEnum['무표정'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['무표정']
                            )
                          },
                          놀람: {
                            name: '놀람',
                            value: StandardFacialExpressionChildOptionValueEnum['놀람'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['놀람']
                            )
                          },
                          고민: {
                            name: '고민',
                            value: StandardFacialExpressionChildOptionValueEnum['고민'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['고민']
                            )
                          },
                          진지: {
                            name: '진지',
                            value: StandardFacialExpressionChildOptionValueEnum['진지'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['진지']
                            )
                          },
                          혐오: {
                            name: '혐오',
                            value: StandardFacialExpressionChildOptionValueEnum['혐오'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['혐오']
                            )
                          },
                          분노: {
                            name: '분노',
                            value: StandardFacialExpressionChildOptionValueEnum['분노'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['분노']
                            )
                          },
                          당황: {
                            name: '당황',
                            value: StandardFacialExpressionChildOptionValueEnum['당황'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['당황']
                            )
                          },
                          공포: {
                            name: '공포',
                            value: StandardFacialExpressionChildOptionValueEnum['공포'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['공포']
                            )
                          },
                          슬픔: {
                            name: '슬픔',
                            value: StandardFacialExpressionChildOptionValueEnum['슬픔'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['슬픔']
                            )
                          },
                          경계: {
                            name: '경계',
                            value: StandardFacialExpressionChildOptionValueEnum['경계'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['경계']
                            )
                          },
                          좌절: {
                            name: '좌절',
                            value: StandardFacialExpressionChildOptionValueEnum['좌절'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['좌절']
                            )
                          },
                          뻔뻔: {
                            name: '뻔뻔',
                            value: StandardFacialExpressionChildOptionValueEnum['뻔뻔'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['뻔뻔']
                            )
                          },
                          수치: {
                            name: '수치',
                            value: StandardFacialExpressionChildOptionValueEnum['수치'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['수치']
                            )
                          },
                          의심: {
                            name: '의심',
                            value: StandardFacialExpressionChildOptionValueEnum['의심'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['의심']
                            )
                          },
                          불안: {
                            name: '불안',
                            value: StandardFacialExpressionChildOptionValueEnum['불안'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['불안']
                            )
                          },
                          절규: {
                            name: '절규',
                            value: StandardFacialExpressionChildOptionValueEnum['절규'],
                            isSelected: imageOptionValue.표정.includes(
                              StandardFacialExpressionChildOptionValueEnum['절규']
                            )
                          }
                        }
                      : null,
                  deforme: Object.prototype.hasOwnProperty.call(imageOptionValue, '데포르메 여부')
                    ? {
                        해당: {
                          name: '해당',
                          value: 'true',
                          isSelected: imageOptionValue['데포르메 여부'] as boolean
                        },
                        미해당: {
                          name: '미해당',
                          value: 'false',
                          isSelected: !imageOptionValue['데포르메 여부'] as boolean
                        }
                      }
                    : null,
                  horizontalAngle:
                    imageOptionValue['가로앵글'] && imageOptionValue['가로앵글'].length > 0
                      ? {
                          정면: {
                            name: '정면',
                            value: HorizontalAngleChildOptionValueEnum['정면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['정면']
                            )
                          },
                          정측면: {
                            name: '정측면',
                            value: HorizontalAngleChildOptionValueEnum['정측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['정측면']
                            )
                          },
                          측면: {
                            name: '측면',
                            value: HorizontalAngleChildOptionValueEnum['측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['측면']
                            )
                          },
                          후측면: {
                            name: '후측면',
                            value: HorizontalAngleChildOptionValueEnum['후측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['후측면']
                            )
                          },
                          후면: {
                            name: '후면',
                            value: HorizontalAngleChildOptionValueEnum['후면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['후면']
                            )
                          }
                        }
                      : null,
                  postureCategory:
                    imageOptionValue['동작구분'] && imageOptionValue['동작구분'].length > 0
                      ? {
                          없음: {
                            name: '없음',
                            value: PostureCategoryChildOptionValueEnum['없음'],
                            isSelected: imageOptionValue['동작구분'].includes(
                              PostureCategoryChildOptionValueEnum['없음']
                            )
                          },
                          서있음: {
                            name: '서있음',
                            value: PostureCategoryChildOptionValueEnum['서있음'],
                            isSelected: imageOptionValue['동작구분'].includes(
                              PostureCategoryChildOptionValueEnum['서있음']
                            )
                          },
                          앉음: {
                            name: '앉음',
                            value: PostureCategoryChildOptionValueEnum['앉음'],
                            isSelected: imageOptionValue['동작구분'].includes(
                              PostureCategoryChildOptionValueEnum['앉음']
                            )
                          },
                          누움: {
                            name: '누움',
                            value: PostureCategoryChildOptionValueEnum['누움'],
                            isSelected: imageOptionValue['동작구분'].includes(
                              PostureCategoryChildOptionValueEnum['누움']
                            )
                          },
                          엎드림: {
                            name: '엎드림',
                            value: PostureCategoryChildOptionValueEnum['엎드림'],
                            isSelected: imageOptionValue['동작구분'].includes(
                              PostureCategoryChildOptionValueEnum['엎드림']
                            )
                          }
                        }
                      : null,
                  skinship: Object.prototype.hasOwnProperty.call(imageOptionValue, '스킨십')
                    ? {
                        해당: {
                          name: '해당',
                          value: 'true',
                          isSelected: imageOptionValue['스킨십'] as boolean
                        },
                        미해당: {
                          name: '미해당',
                          value: 'false',
                          isSelected: !imageOptionValue['스킨십'] as boolean
                        }
                      }
                    : null
                },
                originalTags: metaTag.map((tag) => tag.name),
                tags: metaTag.map((tag) => tag.name)
              };
            });
            return newList;
          });
        } else if (category === CategorySelectEnum.ADULTS_ONLY) {
          setImageList((prev) => {
            // 기존 리스트 복사
            const newList = prev.map((item) => {
              if (item.imageId !== targetImage.imageId) return item;

              const { image, imageOptionValue, metaTag } = fetchedImageDetailData;

              setSelectedSexualPosition(imageOptionValue.체위 as SexualPositionChildOptionValueEnum);
              setOriginalSelectedSexualPosition(imageOptionValue.체위 as SexualPositionChildOptionValueEnum); //PATCH request body에 체위값 실을지 여부 결정하기 위한 비교값

              // 옵션 및 태그값 업데이트 여부 확인을 위해 original 프로퍼티를 함께 생성
              return {
                ...item,
                isReported: image.isReported,
                originalChildOptions: {
                  sexualPosition: imageOptionValue.체위,
                  femaleBody:
                    imageOptionValue.여체 && imageOptionValue.여체.length > 0
                      ? {
                          슬랜더: {
                            name: '슬랜더',
                            value: FemaleBodyChildOptionValueEnum['슬랜더'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슬랜더']
                          },
                          글래머: {
                            name: '글래머',
                            value: FemaleBodyChildOptionValueEnum['글래머'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['글래머']
                          },
                          슈퍼글래머: {
                            name: '슈퍼글래머',
                            value: FemaleBodyChildOptionValueEnum['슈퍼글래머'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슈퍼글래머']
                          },
                          통통: {
                            name: '통통',
                            value: FemaleBodyChildOptionValueEnum['통통'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['통통']
                          }
                        }
                      : null,
                  sexualBodyPart:
                    imageOptionValue.신체부위 && imageOptionValue.신체부위.length > 0
                      ? {
                          가슴: {
                            name: '가슴',
                            value: SexualBodyPartChildOptionValueEnum['가슴'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['가슴'])
                          },
                          엉덩이: {
                            name: '엉덩이',
                            value: SexualBodyPartChildOptionValueEnum['엉덩이'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['엉덩이'])
                          },
                          '여자 성기': {
                            name: '여자 성기',
                            value: SexualBodyPartChildOptionValueEnum['여자 성기'],
                            isSelected: imageOptionValue.신체부위.includes(
                              SexualBodyPartChildOptionValueEnum['여자 성기']
                            )
                          },
                          '남자 성기': {
                            name: '남자 성기',
                            value: SexualBodyPartChildOptionValueEnum['남자 성기'],
                            isSelected: imageOptionValue.신체부위.includes(
                              SexualBodyPartChildOptionValueEnum['남자 성기']
                            )
                          },
                          입: {
                            name: '입',
                            value: SexualBodyPartChildOptionValueEnum['입'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['입'])
                          },
                          얼굴: {
                            name: '얼굴',
                            value: SexualBodyPartChildOptionValueEnum['얼굴'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['얼굴'])
                          }
                        }
                      : null,
                  characterNumber:
                    imageOptionValue['남성 인원수'] != null &&
                    imageOptionValue['여성 인원수'] != null &&
                    !(imageOptionValue['남성 인원수'] === 0 && imageOptionValue['여성 인원수'] === 0)
                      ? {
                          maleNumber: imageOptionValue['남성 인원수'],
                          femaleNumber: imageOptionValue['여성 인원수']
                        }
                      : null,
                  shot:
                    imageOptionValue['샷 구분'] && imageOptionValue['샷 구분'].length > 0
                      ? {
                          풀샷: {
                            name: '풀샷',
                            value: AdultsOnlyShotChildOptionValueEnum['풀샷'],
                            isSelected: imageOptionValue['샷 구분'] === AdultsOnlyShotChildOptionValueEnum['풀샷']
                          },
                          미디엄샷: {
                            name: '미디엄샷',
                            value: AdultsOnlyShotChildOptionValueEnum['미디엄샷'],
                            isSelected: imageOptionValue['샷 구분'] === AdultsOnlyShotChildOptionValueEnum['미디엄샷']
                          },
                          클로즈업: {
                            name: '클로즈업',
                            value: AdultsOnlyShotChildOptionValueEnum['클로즈업'],
                            isSelected: imageOptionValue['샷 구분'] === AdultsOnlyShotChildOptionValueEnum['클로즈업']
                          }
                        }
                      : null,
                  horizontalAngle:
                    imageOptionValue['가로앵글'] && imageOptionValue['가로앵글'].length > 0
                      ? {
                          정면: {
                            name: '정면',
                            value: HorizontalAngleChildOptionValueEnum['정면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['정면']
                            )
                          },
                          정측면: {
                            name: '정측면',
                            value: HorizontalAngleChildOptionValueEnum['정측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['정측면']
                            )
                          },
                          측면: {
                            name: '측면',
                            value: HorizontalAngleChildOptionValueEnum['측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['측면']
                            )
                          },
                          후측면: {
                            name: '후측면',
                            value: HorizontalAngleChildOptionValueEnum['후측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['후측면']
                            )
                          },
                          후면: {
                            name: '후면',
                            value: HorizontalAngleChildOptionValueEnum['후면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['후면']
                            )
                          }
                        }
                      : null,
                  verticalAngle:
                    imageOptionValue['세로앵글'] && imageOptionValue['세로앵글'].length > 0
                      ? {
                          로우앵글: {
                            name: '로우앵글',
                            value: VerticalAngleChildOptionValueEnum['로우앵글'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['로우앵글']
                          },
                          하이앵글: {
                            name: '하이앵글',
                            value: VerticalAngleChildOptionValueEnum['하이앵글'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['하이앵글']
                          },
                          항공샷: {
                            name: '항공샷',
                            value: VerticalAngleChildOptionValueEnum['항공샷'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['항공샷']
                          },
                          평시각: {
                            name: '평시각',
                            value: VerticalAngleChildOptionValueEnum['평시각'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['평시각']
                          }
                        }
                      : null
                },
                childOptions: {
                  sexualPosition: imageOptionValue.체위,
                  femaleBody:
                    imageOptionValue.여체 && imageOptionValue.여체.length > 0
                      ? {
                          슬랜더: {
                            name: '슬랜더',
                            value: FemaleBodyChildOptionValueEnum['슬랜더'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슬랜더']
                          },
                          글래머: {
                            name: '글래머',
                            value: FemaleBodyChildOptionValueEnum['글래머'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['글래머']
                          },
                          슈퍼글래머: {
                            name: '슈퍼글래머',
                            value: FemaleBodyChildOptionValueEnum['슈퍼글래머'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슈퍼글래머']
                          },
                          통통: {
                            name: '통통',
                            value: FemaleBodyChildOptionValueEnum['통통'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['통통']
                          }
                        }
                      : null,
                  sexualBodyPart:
                    imageOptionValue.신체부위 && imageOptionValue.신체부위.length > 0
                      ? {
                          가슴: {
                            name: '가슴',
                            value: SexualBodyPartChildOptionValueEnum['가슴'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['가슴'])
                          },
                          엉덩이: {
                            name: '엉덩이',
                            value: SexualBodyPartChildOptionValueEnum['엉덩이'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['엉덩이'])
                          },
                          '여자 성기': {
                            name: '여자 성기',
                            value: SexualBodyPartChildOptionValueEnum['여자 성기'],
                            isSelected: imageOptionValue.신체부위.includes(
                              SexualBodyPartChildOptionValueEnum['여자 성기']
                            )
                          },
                          '남자 성기': {
                            name: '남자 성기',
                            value: SexualBodyPartChildOptionValueEnum['남자 성기'],
                            isSelected: imageOptionValue.신체부위.includes(
                              SexualBodyPartChildOptionValueEnum['남자 성기']
                            )
                          },
                          입: {
                            name: '입',
                            value: SexualBodyPartChildOptionValueEnum['입'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['입'])
                          },
                          얼굴: {
                            name: '얼굴',
                            value: SexualBodyPartChildOptionValueEnum['얼굴'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['얼굴'])
                          }
                        }
                      : null,
                  characterNumber:
                    imageOptionValue['남성 인원수'] != null &&
                    imageOptionValue['여성 인원수'] != null &&
                    !(imageOptionValue['남성 인원수'] === 0 && imageOptionValue['여성 인원수'] === 0)
                      ? {
                          maleNumber: imageOptionValue['남성 인원수'],
                          femaleNumber: imageOptionValue['여성 인원수']
                        }
                      : null,
                  shot:
                    imageOptionValue['샷 구분'] && imageOptionValue['샷 구분'].length > 0
                      ? {
                          풀샷: {
                            name: '풀샷',
                            value: AdultsOnlyShotChildOptionValueEnum['풀샷'],
                            isSelected: imageOptionValue['샷 구분'] === AdultsOnlyShotChildOptionValueEnum['풀샷']
                          },
                          미디엄샷: {
                            name: '미디엄샷',
                            value: AdultsOnlyShotChildOptionValueEnum['미디엄샷'],
                            isSelected: imageOptionValue['샷 구분'] === AdultsOnlyShotChildOptionValueEnum['미디엄샷']
                          },
                          클로즈업: {
                            name: '클로즈업',
                            value: AdultsOnlyShotChildOptionValueEnum['클로즈업'],
                            isSelected: imageOptionValue['샷 구분'] === AdultsOnlyShotChildOptionValueEnum['클로즈업']
                          }
                        }
                      : null,
                  horizontalAngle:
                    imageOptionValue['가로앵글'] && imageOptionValue['가로앵글'].length > 0
                      ? {
                          정면: {
                            name: '정면',
                            value: HorizontalAngleChildOptionValueEnum['정면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['정면']
                            )
                          },
                          정측면: {
                            name: '정측면',
                            value: HorizontalAngleChildOptionValueEnum['정측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['정측면']
                            )
                          },
                          측면: {
                            name: '측면',
                            value: HorizontalAngleChildOptionValueEnum['측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['측면']
                            )
                          },
                          후측면: {
                            name: '후측면',
                            value: HorizontalAngleChildOptionValueEnum['후측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['후측면']
                            )
                          },
                          후면: {
                            name: '후면',
                            value: HorizontalAngleChildOptionValueEnum['후면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['후면']
                            )
                          }
                        }
                      : null,
                  verticalAngle:
                    imageOptionValue['세로앵글'] && imageOptionValue['세로앵글'].length > 0
                      ? {
                          로우앵글: {
                            name: '로우앵글',
                            value: VerticalAngleChildOptionValueEnum['로우앵글'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['로우앵글']
                          },
                          하이앵글: {
                            name: '하이앵글',
                            value: VerticalAngleChildOptionValueEnum['하이앵글'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['하이앵글']
                          },
                          항공샷: {
                            name: '항공샷',
                            value: VerticalAngleChildOptionValueEnum['항공샷'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['항공샷']
                          },
                          평시각: {
                            name: '평시각',
                            value: VerticalAngleChildOptionValueEnum['평시각'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['평시각']
                          }
                        }
                      : null
                },
                originalTags: metaTag.map((tag) => tag.name),
                tags: metaTag.map((tag) => tag.name)
              };
            });
            return newList;
          });
        }
      });
    }

    if (!targetImage.aiRecommendedChildOptions) {
      getAIRecommendation(targetImage, targetImageIndex, category);
    }
  };

  const sendPostPresignedUrl = async (targetImage: IUploadImageListItem, targetImageIndex: number) => {
    const requestBody = [{ name: targetImage.name, type: targetImage.type }];

    await postPresignedUrlMutateAsync(requestBody)
      .then(async (res) => {
        // POST /images/presigned-url 응답의 key와 presignedUrl 프로퍼티를 imageList 요소에 할당
        const presignedData = res.data.data;

        // POST 및 PATCH /admin/images 요청을 위해 key와 presignedUrl 프로퍼티를 imageList 요소에 할당
        setImageList((prev) => {
          const imageListWithKeyAndPresignedUrl = prev.map((item, idx) => {
            if (idx === targetImageIndex) {
              return {
                ...item,
                key: presignedData[0].key,
                presignedUrl: presignedData[0].presignedUrl
              };
            }
            return item;
          });

          // 업데이트된 이미지로 S3 업로드 실행
          uploadImageToS3(imageListWithKeyAndPresignedUrl[targetImageIndex], targetImageIndex);

          return imageListWithKeyAndPresignedUrl;
        });
      })
      .catch((error) => {
        console.log('postPresignedUrlMutateAsync error: ', error);

        // 해당 이미지의 key와 presignedUrl값을 imageList 요소로부터 삭제
        setImageList((prev) => {
          return prev.map((item, idx) => {
            if (idx === targetImageIndex) {
              const { key, presignedUrl, ...rest } = item;
              return rest;
            }
            return item;
          });
        });

        setModalType(AdminModalType.IMAGE_UPLOAD_REQUEST_FAIL);
        setModalTitle('업로드에 실패하였습니다.');
        setModalContent('요청이 실패하였습니다. 다시 시도해주세요.');
        setIsModalVisible(true);
        setIsRequestPending(false); //저장버튼 활성화
      });
  };

  const uploadImageToS3 = async (targetImage: IUploadImageListItem, targetImageIndex: number) => {
    await fetch(targetImage.presignedUrl ?? '', {
      method: 'PUT',
      body: targetImage.file,
      headers: { 'Content-Type': targetImage.type }
    })
      .then(() => {
        if (!targetImage.aiRecommendedChildOptions) {
          getAIRecommendation(targetImage, targetImageIndex, category);
        }
      })
      .catch((error) => {
        console.log('uploadImageToS3 error: ', error);

        // 해당 이미지를 imageList로부터 삭제
        setImageList((prev) => {
          return prev.filter((_, idx) => idx !== targetImageIndex);
        });
      });
  };

  const getParentOptionId = (categoryParam: CategorySelectEnum) => {
    if (categoryParam === CategorySelectEnum.STANDARD) {
      return optionsConstant.parentOptions.STANDARD.id;
    } else if (categoryParam === CategorySelectEnum.ADULTS_ONLY) {
      return optionsConstant.parentOptions.ADULTS_ONLY.id;
    } else {
      return optionsConstant.parentOptions.STANDARD.id;
    }
  };

  const getAIRecommendation = async (
    targetImage: IUploadImageListItem,
    targetImageIndex: number,
    categoryParam: CategorySelectEnum
  ) => {
    let s3Path, key;

    // 새로 업로드된 이미지는 key값을 갖는다
    if (targetImage.key) {
      // 가장 먼저 등장하는 슬래시의 인덱스를 찾는다
      const slashIndex = targetImage.key.indexOf('/');

      if (slashIndex !== -1) {
        s3Path = targetImage.key.slice(0, slashIndex); // 슬래시 이전 부분 'temp'
        key = targetImage.key.slice(slashIndex + 1); // 슬래시 이후 부분 e.g. IMG-uuid-fileName.png
      } else {
        // 슬래시가 없는 경우 전체를 파일명으로 간주
        s3Path = '';
        key = targetImage.key;
      }
    }
    // 기존에 업로드된 이미지는 uniqueFileName값을 갖는다
    else if (targetImage.uniqueFileName) {
      s3Path = 'images';
      key = targetImage.uniqueFileName;
    }

    if (s3Path && key) {
      await fetchImageAIRecommendationMutateAsync({
        s3Path: s3Path,
        key: key,
        parentOptionId: getParentOptionId(categoryParam)
      })
        .then((res) => {
          const fetchedAIRecommendationData = res.data.data;

          if (categoryParam === CategorySelectEnum.STANDARD) {
            setImageList((prev) => {
              // 기존 리스트 복사
              const newList = prev.map((item, index) => {
                if (index !== targetImageIndex) return item;

                const { imageOptionValue, tags } = fetchedAIRecommendationData;

                return {
                  ...item,
                  aiRecommendedChildOptions: {
                    backgroundCut: Object.prototype.hasOwnProperty.call(imageOptionValue, '배경컷')
                      ? {
                          해당: { name: '해당', value: 'true', isSelected: imageOptionValue['배경컷'] as boolean },
                          미해당: { name: '미해당', value: 'false', isSelected: !imageOptionValue['배경컷'] as boolean }
                        }
                      : null,
                    characterNumber:
                      imageOptionValue['남성 인원수'] != null &&
                      imageOptionValue['여성 인원수'] != null &&
                      !(imageOptionValue['남성 인원수'] === 0 && imageOptionValue['여성 인원수'] === 0)
                        ? {
                            maleNumber: imageOptionValue['남성 인원수'],
                            femaleNumber: imageOptionValue['여성 인원수']
                          }
                        : null,
                    shot:
                      imageOptionValue['샷 구분'] && imageOptionValue['샷 구분'].length > 0
                        ? {
                            풀샷: {
                              name: '풀샷',
                              value: StandardShotChildOptionValueEnum['풀샷'],
                              isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['풀샷']
                            },
                            미디엄샷: {
                              name: '미디엄샷',
                              value: StandardShotChildOptionValueEnum['미디엄샷'],
                              isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['미디엄샷']
                            },
                            클로즈업: {
                              name: '클로즈업',
                              value: StandardShotChildOptionValueEnum['클로즈업'],
                              isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['클로즈업']
                            },
                            롱샷: {
                              name: '롱샷',
                              value: StandardShotChildOptionValueEnum['롱샷'],
                              isSelected: imageOptionValue['샷 구분'] === StandardShotChildOptionValueEnum['롱샷']
                            }
                          }
                        : null,
                    directed: Object.prototype.hasOwnProperty.call(imageOptionValue, '연출')
                      ? {
                          해당: { name: '해당', value: 'true', isSelected: imageOptionValue['연출'] as boolean },
                          미해당: { name: '미해당', value: 'false', isSelected: !imageOptionValue['연출'] as boolean }
                        }
                      : null,
                    verticalAngle:
                      imageOptionValue['세로앵글'] && imageOptionValue['세로앵글'].length > 0
                        ? {
                            로우앵글: {
                              name: '로우앵글',
                              value: VerticalAngleChildOptionValueEnum['로우앵글'],
                              isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['로우앵글']
                            },
                            하이앵글: {
                              name: '하이앵글',
                              value: VerticalAngleChildOptionValueEnum['하이앵글'],
                              isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['하이앵글']
                            },
                            항공샷: {
                              name: '항공샷',
                              value: VerticalAngleChildOptionValueEnum['항공샷'],
                              isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['항공샷']
                            },
                            평시각: {
                              name: '평시각',
                              value: VerticalAngleChildOptionValueEnum['평시각'],
                              isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['평시각']
                            }
                          }
                        : null,
                    ageRange:
                      imageOptionValue['연령대'] &&
                      imageOptionValue['연령대'].length > 0 &&
                      imageOptionValue['연령대'][0].length > 0
                        ? {
                            유아: {
                              name: '유아',
                              value: AgeRangeChildOptionValueEnum['유아'],
                              isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['유아'])
                            },
                            청소년: {
                              name: '청소년',
                              value: AgeRangeChildOptionValueEnum['청소년'],
                              isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['청소년'])
                            },
                            청년: {
                              name: '청년',
                              value: AgeRangeChildOptionValueEnum['청년'],
                              isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['청년'])
                            },
                            중장년: {
                              name: '중장년',
                              value: AgeRangeChildOptionValueEnum['중장년'],
                              isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['중장년'])
                            },
                            노인: {
                              name: '노인',
                              value: AgeRangeChildOptionValueEnum['노인'],
                              isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['노인'])
                            }
                          }
                        : null,
                    mainMotionPart:
                      imageOptionValue['중심동작부위'] &&
                      imageOptionValue['중심동작부위'].length > 0 &&
                      imageOptionValue['중심동작부위'][0].length > 0
                        ? {
                            손: {
                              name: '손',
                              value: MainMotionPartChildOptionValueEnum['손'],
                              isSelected: imageOptionValue.중심동작부위.includes(
                                MainMotionPartChildOptionValueEnum['손']
                              )
                            },
                            팔: {
                              name: '팔',
                              value: MainMotionPartChildOptionValueEnum['팔'],
                              isSelected: imageOptionValue.중심동작부위.includes(
                                MainMotionPartChildOptionValueEnum['팔']
                              )
                            },
                            다리: {
                              name: '다리',
                              value: MainMotionPartChildOptionValueEnum['다리'],
                              isSelected: imageOptionValue.중심동작부위.includes(
                                MainMotionPartChildOptionValueEnum['다리']
                              )
                            },
                            발: {
                              name: '발',
                              value: MainMotionPartChildOptionValueEnum['발'],
                              isSelected: imageOptionValue.중심동작부위.includes(
                                MainMotionPartChildOptionValueEnum['발']
                              )
                            },
                            머리: {
                              name: '머리',
                              value: MainMotionPartChildOptionValueEnum['머리'],
                              isSelected: imageOptionValue.중심동작부위.includes(
                                MainMotionPartChildOptionValueEnum['머리']
                              )
                            },
                            전신: {
                              name: '전신',
                              value: MainMotionPartChildOptionValueEnum['전신'],
                              isSelected: imageOptionValue.중심동작부위.includes(
                                MainMotionPartChildOptionValueEnum['전신']
                              )
                            },
                            상체: {
                              name: '상체',
                              value: MainMotionPartChildOptionValueEnum['상체'],
                              isSelected: imageOptionValue.중심동작부위.includes(
                                MainMotionPartChildOptionValueEnum['상체']
                              )
                            },
                            하체: {
                              name: '하체',
                              value: MainMotionPartChildOptionValueEnum['하체'],
                              isSelected: imageOptionValue.중심동작부위.includes(
                                MainMotionPartChildOptionValueEnum['하체']
                              )
                            }
                          }
                        : null,
                    facialExpression:
                      imageOptionValue['표정'] &&
                      imageOptionValue['표정'].length > 0 &&
                      imageOptionValue['표정'][0].length > 0
                        ? {
                            없음: {
                              name: '없음',
                              value: StandardFacialExpressionChildOptionValueEnum['없음'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['없음']
                              )
                            },
                            만족: {
                              name: '만족',
                              value: StandardFacialExpressionChildOptionValueEnum['만족'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['만족']
                              )
                            },
                            미소: {
                              name: '미소',
                              value: StandardFacialExpressionChildOptionValueEnum['미소'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['미소']
                              )
                            },
                            새침: {
                              name: '새침',
                              value: StandardFacialExpressionChildOptionValueEnum['새침'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['새침']
                              )
                            },
                            기쁨: {
                              name: '기쁨',
                              value: StandardFacialExpressionChildOptionValueEnum['기쁨'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['기쁨']
                              )
                            },
                            폭소: {
                              name: '폭소',
                              value: StandardFacialExpressionChildOptionValueEnum['폭소'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['폭소']
                              )
                            },
                            수줍음: {
                              name: '수줍음',
                              value: StandardFacialExpressionChildOptionValueEnum['수줍음'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['수줍음']
                              )
                            },
                            무표정: {
                              name: '무표정',
                              value: StandardFacialExpressionChildOptionValueEnum['무표정'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['무표정']
                              )
                            },
                            놀람: {
                              name: '놀람',
                              value: StandardFacialExpressionChildOptionValueEnum['놀람'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['놀람']
                              )
                            },
                            고민: {
                              name: '고민',
                              value: StandardFacialExpressionChildOptionValueEnum['고민'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['고민']
                              )
                            },
                            진지: {
                              name: '진지',
                              value: StandardFacialExpressionChildOptionValueEnum['진지'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['진지']
                              )
                            },
                            혐오: {
                              name: '혐오',
                              value: StandardFacialExpressionChildOptionValueEnum['혐오'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['혐오']
                              )
                            },
                            분노: {
                              name: '분노',
                              value: StandardFacialExpressionChildOptionValueEnum['분노'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['분노']
                              )
                            },
                            당황: {
                              name: '당황',
                              value: StandardFacialExpressionChildOptionValueEnum['당황'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['당황']
                              )
                            },
                            공포: {
                              name: '공포',
                              value: StandardFacialExpressionChildOptionValueEnum['공포'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['공포']
                              )
                            },
                            슬픔: {
                              name: '슬픔',
                              value: StandardFacialExpressionChildOptionValueEnum['슬픔'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['슬픔']
                              )
                            },
                            경계: {
                              name: '경계',
                              value: StandardFacialExpressionChildOptionValueEnum['경계'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['경계']
                              )
                            },
                            좌절: {
                              name: '좌절',
                              value: StandardFacialExpressionChildOptionValueEnum['좌절'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['좌절']
                              )
                            },
                            뻔뻔: {
                              name: '뻔뻔',
                              value: StandardFacialExpressionChildOptionValueEnum['뻔뻔'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['뻔뻔']
                              )
                            },
                            수치: {
                              name: '수치',
                              value: StandardFacialExpressionChildOptionValueEnum['수치'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['수치']
                              )
                            },
                            의심: {
                              name: '의심',
                              value: StandardFacialExpressionChildOptionValueEnum['의심'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['의심']
                              )
                            },
                            불안: {
                              name: '불안',
                              value: StandardFacialExpressionChildOptionValueEnum['불안'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['불안']
                              )
                            },
                            절규: {
                              name: '절규',
                              value: StandardFacialExpressionChildOptionValueEnum['절규'],
                              isSelected: imageOptionValue.표정.includes(
                                StandardFacialExpressionChildOptionValueEnum['절규']
                              )
                            }
                          }
                        : null,
                    deforme: Object.prototype.hasOwnProperty.call(imageOptionValue, '데포르메 여부')
                      ? {
                          해당: {
                            name: '해당',
                            value: 'true',
                            isSelected: imageOptionValue['데포르메 여부'] as boolean
                          },
                          미해당: {
                            name: '미해당',
                            value: 'false',
                            isSelected: !imageOptionValue['데포르메 여부'] as boolean
                          }
                        }
                      : null,
                    horizontalAngle:
                      imageOptionValue['가로앵글'] &&
                      imageOptionValue['가로앵글'].length > 0 &&
                      imageOptionValue['가로앵글'][0].length > 0
                        ? {
                            정면: {
                              name: '정면',
                              value: HorizontalAngleChildOptionValueEnum['정면'],
                              isSelected: imageOptionValue['가로앵글'].includes(
                                HorizontalAngleChildOptionValueEnum['정면']
                              )
                            },
                            정측면: {
                              name: '정측면',
                              value: HorizontalAngleChildOptionValueEnum['정측면'],
                              isSelected: imageOptionValue['가로앵글'].includes(
                                HorizontalAngleChildOptionValueEnum['정측면']
                              )
                            },
                            측면: {
                              name: '측면',
                              value: HorizontalAngleChildOptionValueEnum['측면'],
                              isSelected: imageOptionValue['가로앵글'].includes(
                                HorizontalAngleChildOptionValueEnum['측면']
                              )
                            },
                            후측면: {
                              name: '후측면',
                              value: HorizontalAngleChildOptionValueEnum['후측면'],
                              isSelected: imageOptionValue['가로앵글'].includes(
                                HorizontalAngleChildOptionValueEnum['후측면']
                              )
                            },
                            후면: {
                              name: '후면',
                              value: HorizontalAngleChildOptionValueEnum['후면'],
                              isSelected: imageOptionValue['가로앵글'].includes(
                                HorizontalAngleChildOptionValueEnum['후면']
                              )
                            }
                          }
                        : null,
                    postureCategory:
                      imageOptionValue['동작구분'] &&
                      imageOptionValue['동작구분'].length > 0 &&
                      imageOptionValue['동작구분'][0].length > 0
                        ? {
                            없음: {
                              name: '없음',
                              value: PostureCategoryChildOptionValueEnum['없음'],
                              isSelected: imageOptionValue['동작구분'].includes(
                                PostureCategoryChildOptionValueEnum['없음']
                              )
                            },
                            서있음: {
                              name: '서있음',
                              value: PostureCategoryChildOptionValueEnum['서있음'],
                              isSelected: imageOptionValue['동작구분'].includes(
                                PostureCategoryChildOptionValueEnum['서있음']
                              )
                            },
                            앉음: {
                              name: '앉음',
                              value: PostureCategoryChildOptionValueEnum['앉음'],
                              isSelected: imageOptionValue['동작구분'].includes(
                                PostureCategoryChildOptionValueEnum['앉음']
                              )
                            },
                            누움: {
                              name: '누움',
                              value: PostureCategoryChildOptionValueEnum['누움'],
                              isSelected: imageOptionValue['동작구분'].includes(
                                PostureCategoryChildOptionValueEnum['누움']
                              )
                            },
                            엎드림: {
                              name: '엎드림',
                              value: PostureCategoryChildOptionValueEnum['엎드림'],
                              isSelected: imageOptionValue['동작구분'].includes(
                                PostureCategoryChildOptionValueEnum['엎드림']
                              )
                            }
                          }
                        : null,
                    skinship: Object.prototype.hasOwnProperty.call(imageOptionValue, '스킨십')
                      ? {
                          해당: {
                            name: '해당',
                            value: 'true',
                            isSelected: imageOptionValue['스킨십'] as boolean
                          },
                          미해당: {
                            name: '미해당',
                            value: 'false',
                            isSelected: !imageOptionValue['스킨십'] as boolean
                          }
                        }
                      : null
                  },
                  aiRecommendedTags: tags ?? []
                };
              });
              return newList;
            });
          } else if (categoryParam === CategorySelectEnum.ADULTS_ONLY) {
            setImageList((prev) => {
              // 기존 리스트 복사
              const newList = prev.map((item, index) => {
                if (index !== targetImageIndex) return item;

                const { imageOptionValue, tags } = fetchedAIRecommendationData;

                return {
                  ...item,
                  aiRecommendedChildOptions: {
                    sexualPosition: selectedSexualPosition,
                    //femaleBody, shot, horizontalAngle, verticalAngle의 경우 빈 문자열 응답시 null값 부여
                    femaleBody:
                      imageOptionValue.여체 && imageOptionValue.여체.length > 0
                        ? {
                            슬랜더: {
                              name: '슬랜더',
                              value: FemaleBodyChildOptionValueEnum['슬랜더'],
                              isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슬랜더']
                            },
                            글래머: {
                              name: '글래머',
                              value: FemaleBodyChildOptionValueEnum['글래머'],
                              isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['글래머']
                            },
                            슈퍼글래머: {
                              name: '슈퍼글래머',
                              value: FemaleBodyChildOptionValueEnum['슈퍼글래머'],
                              isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슈퍼글래머']
                            },
                            통통: {
                              name: '통통',
                              value: FemaleBodyChildOptionValueEnum['통통'],
                              isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['통통']
                            }
                          }
                        : null,
                    sexualBodyPart:
                      imageOptionValue.신체부위 && imageOptionValue.신체부위.length > 0
                        ? {
                            가슴: {
                              name: '가슴',
                              value: SexualBodyPartChildOptionValueEnum['가슴'],
                              isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['가슴'])
                            },
                            엉덩이: {
                              name: '엉덩이',
                              value: SexualBodyPartChildOptionValueEnum['엉덩이'],
                              isSelected: imageOptionValue.신체부위.includes(
                                SexualBodyPartChildOptionValueEnum['엉덩이']
                              )
                            },
                            '여자 성기': {
                              name: '여자 성기',
                              value: SexualBodyPartChildOptionValueEnum['여자 성기'],
                              isSelected: imageOptionValue.신체부위.includes(
                                SexualBodyPartChildOptionValueEnum['여자 성기']
                              )
                            },
                            '남자 성기': {
                              name: '남자 성기',
                              value: SexualBodyPartChildOptionValueEnum['남자 성기'],
                              isSelected: imageOptionValue.신체부위.includes(
                                SexualBodyPartChildOptionValueEnum['남자 성기']
                              )
                            },
                            입: {
                              name: '입',
                              value: SexualBodyPartChildOptionValueEnum['입'],
                              isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['입'])
                            },
                            얼굴: {
                              name: '얼굴',
                              value: SexualBodyPartChildOptionValueEnum['얼굴'],
                              isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['얼굴'])
                            }
                          }
                        : null,
                    characterNumber:
                      imageOptionValue['남성 인원수'] != null &&
                      imageOptionValue['여성 인원수'] != null &&
                      !(imageOptionValue['남성 인원수'] === 0 && imageOptionValue['여성 인원수'] === 0)
                        ? {
                            maleNumber: imageOptionValue['남성 인원수'],
                            femaleNumber: imageOptionValue['여성 인원수']
                          }
                        : null,
                    shot:
                      imageOptionValue['샷 구분'] && imageOptionValue['샷 구분'].length > 0
                        ? {
                            풀샷: {
                              name: '풀샷',
                              value: AdultsOnlyShotChildOptionValueEnum['풀샷'],
                              isSelected: imageOptionValue['샷 구분'] === AdultsOnlyShotChildOptionValueEnum['풀샷']
                            },
                            미디엄샷: {
                              name: '미디엄샷',
                              value: AdultsOnlyShotChildOptionValueEnum['미디엄샷'],
                              isSelected: imageOptionValue['샷 구분'] === AdultsOnlyShotChildOptionValueEnum['미디엄샷']
                            },
                            클로즈업: {
                              name: '클로즈업',
                              value: AdultsOnlyShotChildOptionValueEnum['클로즈업'],
                              isSelected: imageOptionValue['샷 구분'] === AdultsOnlyShotChildOptionValueEnum['클로즈업']
                            }
                          }
                        : null,
                    horizontalAngle:
                      imageOptionValue['가로앵글'] && imageOptionValue['가로앵글'].length > 0
                        ? {
                            정면: {
                              name: '정면',
                              value: HorizontalAngleChildOptionValueEnum['정면'],
                              isSelected: imageOptionValue['가로앵글'].includes(
                                HorizontalAngleChildOptionValueEnum['정면']
                              )
                            },
                            정측면: {
                              name: '정측면',
                              value: HorizontalAngleChildOptionValueEnum['정측면'],
                              isSelected: imageOptionValue['가로앵글'].includes(
                                HorizontalAngleChildOptionValueEnum['정측면']
                              )
                            },
                            측면: {
                              name: '측면',
                              value: HorizontalAngleChildOptionValueEnum['측면'],
                              isSelected: imageOptionValue['가로앵글'].includes(
                                HorizontalAngleChildOptionValueEnum['측면']
                              )
                            },
                            후측면: {
                              name: '후측면',
                              value: HorizontalAngleChildOptionValueEnum['후측면'],
                              isSelected: imageOptionValue['가로앵글'].includes(
                                HorizontalAngleChildOptionValueEnum['후측면']
                              )
                            },
                            후면: {
                              name: '후면',
                              value: HorizontalAngleChildOptionValueEnum['후면'],
                              isSelected: imageOptionValue['가로앵글'].includes(
                                HorizontalAngleChildOptionValueEnum['후면']
                              )
                            }
                          }
                        : null,
                    verticalAngle:
                      imageOptionValue['세로앵글'] && imageOptionValue['세로앵글'].length > 0
                        ? {
                            로우앵글: {
                              name: '로우앵글',
                              value: VerticalAngleChildOptionValueEnum['로우앵글'],
                              isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['로우앵글']
                            },
                            하이앵글: {
                              name: '하이앵글',
                              value: VerticalAngleChildOptionValueEnum['하이앵글'],
                              isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['하이앵글']
                            },
                            항공샷: {
                              name: '항공샷',
                              value: VerticalAngleChildOptionValueEnum['항공샷'],
                              isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['항공샷']
                            },
                            평시각: {
                              name: '평시각',
                              value: VerticalAngleChildOptionValueEnum['평시각'],
                              isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['평시각']
                            }
                          }
                        : null
                  },
                  aiRecommendedTags: tags ?? []
                };
              });
              return newList;
            });
          }
        })
        .catch((error) => {
          console.log('fetchImageAIRecommendationMutateAsync error: ', error);
        });
    }
  };

  // image 파일명 생성 함수
  const createImageName = (
    inputKeyword: string,
    imageId: number,
    listLength: number,
    index: number,
    type: string,
    sequenceId?: number,
    file?: File, //신규 이미지일 시 File값 가짐
    originalFileName?: string //기존 이미지일 시 string값 가짐
  ) => {
    const ext = type.split('/')[1];

    // 작품명 입력 전 파일명 생성
    if (inputKeyword.length === 0) {
      if (file) {
        //신규 이미지일 시
        return file.name;
      } else if (originalFileName) {
        //기존 이미지일 시
        return originalFileName;
      }
    }

    if (listLength !== 1 && sequenceId) {
      return `${inputKeyword}_${sequenceId}_${index + 1}.${ext}`;
    } else {
      return `${inputKeyword}_${imageId + index}.${ext}`;
    }
  };

  const handleSelectedSexualPositionChange = (event: SelectChangeEvent<unknown>) => {
    setSelectedSexualPosition(event.target.value as SexualPositionChildOptionValueEnum);
  };

  // 작품명 인풋 입력시
  const handleInputKeywordChange = (event: React.SyntheticEvent, newValue: string) => {
    // 일반 문자 입력 시: 상태 업데이트
    if (newValue.trim().length > 0) {
      setInputKeyword(newValue);
      handleAutocompleteKeywordChange(newValue);
    } else {
      setInputKeyword('');
      handleAutocompleteKeywordChange(newValue);
    }
  };

  // 작품명 오토컴플리트 리스트 불러오기에 이용할 키워드 setState
  const handleAutocompleteKeywordChange = useCallback(
    debounce((newValue: string) => {
      setAutocompleteKeyword(newValue);
    }, 500),
    []
  );

  // 작품명 오토컴플리트 리스트에서 키워드 선택 시 추가
  const handleAutocompleteChange = (event: React.SyntheticEvent, newValue: string | null) => {
    if (newValue && typeof newValue === 'string' && newValue.trim().length > 0) {
      setAutocompleteKeyword('');
      setInputKeyword(newValue);
      handleComicNameUpdate(newValue);

      // debounce된 함수 호출 취소하여 setAutocompleteKeyword(value) 재실행 방지
      handleAutocompleteKeywordChange.cancel();
    }
  };

  // 작품명 변경 후 SearchInput blur될 시 imageList 각 요소 name을 업데이트
  const handleComicNameUpdate = (value: string) => {
    setImageList((prev) =>
      prev.map((item, idx) => ({
        ...item,
        comic: { ...item.comic, name: value.trim() },
        name: createImageName(
          value.trim(),
          item.imageId ? item.imageId : lastImageId + 1,
          prev.length,
          idx,
          item.type,
          sequenceId ? sequenceId : lastSequenceId + 1,
          item.file,
          item.originalFileName
        )
      }))
    );
  };

  // OS 확인
  const checkIfMacOS = (): boolean => {
    // userAgent 문자열을 통한 OS 확인
    const userAgent = navigator.userAgent.toLowerCase();

    // macOS의 다양한 식별자 확인
    const macIdentifiers = ['macintosh', 'mac os x', 'mac_powerpc'];
    return macIdentifiers.some((identifier) => userAgent.includes(identifier));
  };

  // 파일 유효성 검사 함수
  const validateFiles = (files: File[]) => {
    const validTypes = ['image/png', 'image/jpg', 'image/jpeg'];

    const isMacOS = checkIfMacOS();
    const maxSize = isMacOS
      ? 10 * 1000 * 1000 // MacOS: 10,000,000 bytes
      : 10 * 1024 * 1024; // Windows: 10,485,760 bytes

    const invalidTypeFiles = files.filter((file) => !validTypes.includes(file.type));
    const oversizedFiles = files.filter((file) => file.size > maxSize);

    // 잘못된 형식의 파일이 있는 경우
    if (invalidTypeFiles.length > 0) {
      setModalType(AdminModalType.IMAGE_FILE_TYPE_ERROR);
      setIsModalVisible(true);
      setModalTitle(`${invalidTypeFiles.length}장의 파일 업로드가 실패했습니다.`);
      setModalContent('jpg, png 형식만 등록이 가능합니다.');
      return files.filter((file) => validTypes.includes(file.type) && file.size <= maxSize);
    }

    // 용량 초과 파일이 있는 경우
    if (oversizedFiles.length > 0) {
      setModalType(AdminModalType.IMAGE_FILE_SIZE_ERROR);
      setIsModalVisible(true);
      setModalTitle(`${oversizedFiles.length}장의 파일 업로드가 실패했습니다.`);
      setModalContent('10MB 이하의 이미지만 등록이 가능합니다.');
      return files.filter((file) => file.size <= maxSize && validTypes.includes(file.type));
    }

    return files;
  };

  // '파일 직접 선택' 버튼 클릭시 이미지 첨부
  const handleImageUploadButtonClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const files = Array.from(e.target.files);
    const validFiles = validateFiles(files);
    const sortedFiles = validFiles.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })); //여러 장 업로드시 원본파일명 이름순 자연 정렬

    // 새로 추가될 이미지 객체 생성
    const newImageItems: IUploadImageListItem[] = sortedFiles.map((file) => ({
      name: '', // 아래에서 덮어씀
      url: URL.createObjectURL(file),
      tags: [],
      order: 0, // 아래에서 덮어씀
      comic: { name: inputKeyword },
      childOptions: category === CategorySelectEnum.STANDARD ? null : { sexualPosition: selectedSexualPosition },
      isFocus: false,
      type: file.type,
      file: file
    }));

    // 기존 리스트에도 type 필드 보장
    const oldImageItems = imageList.map((item) => ({
      ...item,
      type: (item as IUploadImageListItem).type || item.name?.split('.').pop() || 'jpg'
    }));

    const mergedList = [...oldImageItems, ...newImageItems];

    setImageList(
      mergedList.map((item, idx) => ({
        ...item,
        name: createImageName(
          inputKeyword,
          item.imageId ? item.imageId : lastImageId + 1,
          mergedList.length,
          idx,
          item.type,
          sequenceId ? sequenceId : lastSequenceId + 1,
          item.file,
          item.originalFileName
        ),
        order: idx + 1
      }))
    );
  };

  // 파일 drag and drop하여 이미지 첨부
  const handleAttachDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const files = Array.from(e.dataTransfer.files);
    const validFiles = validateFiles(files);
    const sortedFiles = validFiles.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })); //여러 장 업로드시 원본파일명 이름순 자연 정렬

    const newImageItems: IUploadImageListItem[] = sortedFiles.map((file) => ({
      name: '', // 아래에서 덮어씀
      url: URL.createObjectURL(file),
      tags: [],
      order: 0, // 아래에서 덮어씀
      comic: { name: inputKeyword },
      childOptions: category === CategorySelectEnum.STANDARD ? null : { sexualPosition: selectedSexualPosition },
      isFocus: false,
      type: file.type,
      file: file
    }));

    const oldImageItems = imageList.map((item) => ({
      ...item,
      type: (item as IUploadImageListItem).type || item.name?.split('.').pop() || 'jpg'
    }));

    const mergedList = [...oldImageItems, ...newImageItems];

    setImageList(
      mergedList.map((item, idx) => ({
        ...item,
        name: createImageName(
          inputKeyword,
          item.imageId ? item.imageId : lastImageId + 1,
          mergedList.length,
          idx,
          item.type,
          sequenceId ? sequenceId : lastSequenceId + 1,
          item.file,
          item.originalFileName
        ),
        order: idx + 1
      }))
    );
  };

  const handleAttachDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleAttachDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // 이미지 순서 변경: 이미지 드래그 시작시 드래그하는 아이템의 인덱스 저장
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
  };

  // 이미지 순서 변경: 드래그 중인 아이템을 다른 아이템 위에 올려놓았을 때
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // 이미지 순서 변경: 드래그 중인 아이템을 드롭했을 때
  const handleDragDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      e.stopPropagation();

      const dragIndex = Number(e.dataTransfer.getData('text/plain'));

      if (dragIndex === dropIndex) return;

      const newImageList: IUploadImageListItem[] = [];

      setImageList((prev) => {
        // type 필드 보장
        const newList = prev.map((item) => ({
          ...item,
          type: (item as IUploadImageListItem).type || item.name?.split('.').pop() || 'jpg'
        }));

        const [draggedItem] = newList.splice(dragIndex, 1); // 드래그된 아이템을 잘라냄
        newList.splice(dropIndex, 0, draggedItem); // 드래그된 아이템을 새로운 자리에 추가

        const updatedList = newList.map((item, idx) => ({
          ...item,
          name: createImageName(
            inputKeyword,
            item.imageId ? item.imageId : lastImageId + 1,
            newList.length,
            idx,
            item.type, // 상단에서 type 필드가 보장되었으므로 항상 유효
            sequenceId ? sequenceId : lastSequenceId + 1,
            item.file,
            item.originalFileName
          ),
          order: idx + 1,
          isFocus: idx === dropIndex
        }));

        newImageList.push(...updatedList);

        return updatedList;
      });

      // newImageList를 사용하여 handleImageFocus 로직 직접 실행
      setFocusedImageIndex(dropIndex);
      const targetImage = newImageList[dropIndex];
      invokeImageApiCall(targetImage, dropIndex);
    },
    [inputKeyword, lastImageId, lastSequenceId]
  );

  // 이미지 삭제 버튼 클릭시
  const handleImageDelete = (isNewlyAttachedImage: boolean) => {
    if (isNewlyAttachedImage) {
      setModalType(AdminModalType.IMAGE_DELETE_CONFIRM);
      setModalTitle('이미지를 삭제하시겠습니까?');
      setModalContent('이 작업은 되돌릴 수 없습니다.');
      setIsModalVisible(true);
    } else {
      setModalType(AdminModalType.IMAGE_DELETE_CONFIRM);
      setModalTitle('이미지를 삭제하시겠습니까?');
      setModalContent('이미지를 삭제한 뒤 ‘저장’을 눌러야 REFA에 최종 반영됩니다.');
      setIsModalVisible(true);
    }
  };

  // childOptions 업데이트 함수
  const updateFocusedImageChildOptions = (key: string, value: any) => {
    if (focusedImageIndex === null) return;

    setImageList((prev) => {
      // 기존 리스트 복사
      const newList = prev.map((item, idx) => {
        if (idx !== focusedImageIndex) return item;
        // childOptions도 새 객체로 복사
        return {
          ...item,
          childOptions: {
            ...item.childOptions,
            [key]: value
          }
        };
      });
      return newList;
    });
  };

  // 의존성 필터 업데이트 함수
  const updateDependentChildOptions = (key: string, value: any) => {
    if (focusedImageIndex === null) return;

    if (key === 'backgroundCut' && value && value.해당 && value.해당?.isSelected) {
      setImageList((prev) => {
        // 기존 리스트 복사
        const newList = prev.map((item, idx) => {
          if (idx !== focusedImageIndex) return item;
          // childOptions도 새 객체로 복사
          return {
            ...item,
            childOptions: {
              ...item.childOptions,
              characterNumber: null,
              ageRange: null,
              mainMotionPart: null,
              facialExpression: null,
              deforme: null,
              horizontalAngle: null,
              postureCategory: null,
              skinship: null
            }
          };
        });
        return newList;
      });
    } else if (key === 'isCharacterNumberOne' && value === true) {
      setImageList((prev) => {
        // 기존 리스트 복사
        const newList = prev.map((item, idx) => {
          if (idx !== focusedImageIndex) return item;
          // childOptions도 새 객체로 복사
          return {
            ...item,
            childOptions: {
              ...item.childOptions,
              skinship: null
            }
          };
        });
        return newList;
      });
    }
  };

  // tags 배열에 태그를 추가하는 함수
  const addFocusedImageTag = (value: string) => {
    if (focusedImageIndex === null) return;
    setImageList((prev) => {
      // 기존 리스트 복사
      const newList = prev.map((item, idx) => {
        if (idx !== focusedImageIndex) return item;
        // 기존 tags 배열에 value가 이미 존재하는지 확인
        if (item.tags && item.tags.includes(value)) return item;
        return {
          ...item,
          tags: item.tags && item.tags.length > 0 ? [...item.tags, value] : [value]
        };
      });
      return newList;
    });
  };

  // 스페이스바 입력 시 태그 추가, handleTagInputKeywordChange보다 먼저 실행됨
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case ' ': {
        e.preventDefault(); // 스페이스 입력 방지
        if (tagInputKeyword.trim().length > 0) {
          addFocusedImageTag(tagInputKeyword.trim());
          setIsTagging(true); // 태그 추가 플래그 설정
          setTagAutocompleteKeyword('');
          setTagInputKeyword('');
        }
        break;
      }
      default: {
        return;
      }
    }
  };

  // 태그 인풋 입력시
  const handleTagInputKeywordChange = (event: React.SyntheticEvent, newValue: string) => {
    if (isTagging) {
      setIsTagging(false); // 태그 추가 후 `onInputChange` 무시
      return;
    }

    setTagInputKeyword(newValue);
    handleTagAutocompleteKeywordChange(newValue.trim());
  };

  // 태그 오토컴플리트 리스트 불러오기에 이용할 키워드 setState
  const handleTagAutocompleteKeywordChange = useCallback(
    debounce((newValue: string) => {
      setTagAutocompleteKeyword(newValue);
    }, 500),
    []
  );

  // 태그 오토컴플리트 리스트에서 키워드 선택 시 추가
  const handleTagAutocompleteChange = (event: React.SyntheticEvent, newValue: string | null) => {
    if (newValue && typeof newValue === 'string' && newValue.trim().length > 0) {
      addFocusedImageTag(newValue.trim());
      setTagAutocompleteKeyword('');
      setTagInputKeyword('');

      // debounce된 함수 호출 취소하여 setTagAutocompleteKeyword(value) 재실행 방지
      handleTagAutocompleteKeywordChange.cancel();
    }
  };

  // tags 배열에 태그를 제거하는 함수
  const deleteFocusedImageTag = (value: string) => {
    if (focusedImageIndex === null) return;
    setImageList((prev) => {
      const newList = prev.map((item, idx) => {
        if (idx !== focusedImageIndex) return item;
        return {
          ...item,
          tags: item.tags && item.tags.length > 0 ? item.tags.filter((tag) => tag !== value) : null
        };
      });
      return newList;
    });
  };

  // child option값 검증
  const validateImageChildOptions = (item: IUploadImageListItem): boolean => {
    let requiredKeys: string[];

    if (category === CategorySelectEnum.STANDARD) {
      const backgroundCut = item.childOptions?.backgroundCut as IOptionList;
      const characterNumber = item.childOptions?.characterNumber as {
        maleNumber: number | string;
        femaleNumber: number | string;
      };

      if (backgroundCut?.해당?.isSelected === true) {
        requiredKeys = ['backgroundCut', 'shot', 'directed', 'verticalAngle'];
      } else if (
        characterNumber &&
        typeof characterNumber.maleNumber === 'number' &&
        typeof characterNumber.femaleNumber === 'number' &&
        characterNumber.maleNumber + characterNumber.femaleNumber === 1
      ) {
        requiredKeys = [
          'backgroundCut',
          'characterNumber',
          'shot',
          'directed',
          'verticalAngle',
          'ageRange',
          'mainMotionPart',
          'facialExpression',
          'deforme',
          'horizontalAngle',
          'postureCategory'
        ];
      } else {
        requiredKeys = [
          'backgroundCut',
          'characterNumber',
          'shot',
          'directed',
          'verticalAngle',
          'ageRange',
          'mainMotionPart',
          'facialExpression',
          'deforme',
          'horizontalAngle',
          'postureCategory',
          'skinship'
        ];
      }
    } else if (category === CategorySelectEnum.ADULTS_ONLY) {
      requiredKeys = ['femaleBody', 'sexualBodyPart', 'characterNumber', 'shot', 'horizontalAngle', 'verticalAngle'];
    } else {
      return false;
    }

    // item이 기존 이미지이며 이미지 타일을 클릭하지 않은 것 = 수정된 값이 없음 = 검증할 필요가 없음 -> return true
    if (item.imageId && !item.originalChildOptions && !item.childOptions) {
      return true;
    } else {
      // item이 신규 이미지이거나 기존 이미지인데 이미지 타일을 클릭한 것 = 수정된 값이 있을 수 있음 = 검증할 필요가 있음 -> 검증 로직 실행
      return requiredKeys.every(
        (key) =>
          item.childOptions && Object.prototype.hasOwnProperty.call(item.childOptions, key) && item.childOptions[key]
      );
    }
  };

  // 저장 버튼을 눌렀을 시 작품명 및 child option값 검증
  const handleSaveButtonClick = () => {
    setIsRequestPending(true);

    const comicNameError = inputKeyword.trim().length === 0;
    let imageChildOptionValueError = true;

    if (imageList.length > 0) {
      imageChildOptionValueError = !imageList.every(validateImageChildOptions);
    } else if (!isCreate && imageList.length === 0) {
      imageChildOptionValueError = false; // 이미지 정보수정 페이지에서는 imageList의 length가 0일 시에도 executeSaveWorkflow 실행
    }

    if (comicNameError || imageChildOptionValueError) {
      setModalType(AdminModalType.OMIT_REQUIRED_INPUT);
      setModalTitle('업로드에 실패했습니다.');
      setModalContent('필수 입력란을 채워주세요.');
      setIsModalVisible(true);
      setIsRequestPending(false); //저장버튼 활성화

      return;
    }

    executeSaveWorkflow(imageList);
  };

  // 저장 버튼을 눌렀을 시 실행하여야 할 API 요청을 촉발
  const executeSaveWorkflow = (imageList: IUploadImageListItem[]) => {
    if (isCreate) {
      sendPostImageList(imageList);
    } else {
      executeEditSaveWorkflow(imageList);
    }
  };

  const sendPostImageList = (imageList: IUploadImageListItem[]) => {
    const requestBody: IPostImageListItem[] = imageList.map((item) => {
      // childOptions 변환

      let childOptions: IChildOptionForPostImageListItem = null;

      if (category === CategorySelectEnum.STANDARD) {
        childOptions = {};

        // 1, 총 인원수 / 2, 남성 인원수 / 3, 여성 인원수
        if (item.childOptions?.characterNumber) {
          const characterNumber = item.childOptions.characterNumber as {
            maleNumber?: number | string;
            femaleNumber?: number | string;
          };
          if (characterNumber.maleNumber !== undefined && characterNumber.femaleNumber !== undefined) {
            childOptions[optionsConstant.childOptions.wholeGenderNumber.id] =
              typeof characterNumber.maleNumber === 'number' &&
              typeof characterNumber.femaleNumber === 'number' &&
              characterNumber.maleNumber + characterNumber.femaleNumber < 4
                ? characterNumber.maleNumber + characterNumber.femaleNumber
                : '4+';
            childOptions[optionsConstant.childOptions.maleNumber.id] = characterNumber.maleNumber;
            childOptions[optionsConstant.childOptions.femaleNumber.id] = characterNumber.femaleNumber;
          }
        }

        // 4, 샷 구분(프레임)
        if (item.childOptions?.shot) {
          const selectedOption = Object.values(item.childOptions.shot).find((option) => option.isSelected);
          if (selectedOption) {
            childOptions[optionsConstant.childOptions.shot.id] = selectedOption.value;
          }
        }

        // 5, 연출
        if (item.childOptions?.directed) {
          const selectedOption = Object.values(item.childOptions.directed).find((option) => option.isSelected);
          if (selectedOption) {
            childOptions[optionsConstant.childOptions.directed.id] = selectedOption.value;
          }
        }

        // 6, 연령대
        if (item.childOptions?.ageRange) {
          const selectedOptions = Object.values(item.childOptions.ageRange)
            .filter((option) => option.isSelected)
            .map((option) => String(option.value));
          if (selectedOptions.length > 0) {
            childOptions[optionsConstant.childOptions.ageRange.id] = selectedOptions;
          }
        }

        // 8, 스킨십
        if (item.childOptions?.skinship) {
          const selectedOption = Object.values(item.childOptions.skinship).find((option) => option.isSelected);
          if (selectedOption) {
            childOptions[optionsConstant.childOptions.skinship.id] = selectedOption.value;
          }
        }

        // 9, 중심동작부위
        if (item.childOptions?.mainMotionPart) {
          const selectedOptions = Object.values(item.childOptions.mainMotionPart)
            .filter((option) => option.isSelected)
            .map((option) => String(option.value));
          if (selectedOptions.length > 0) {
            childOptions[optionsConstant.childOptions.mainMotionPart.id] = selectedOptions;
          }
        }

        // 10, 표정
        if (item.childOptions?.facialExpression) {
          const selectedOptions = Object.values(item.childOptions.facialExpression)
            .filter((option) => option.isSelected)
            .map((option) => String(option.value));
          if (selectedOptions.length > 0) {
            childOptions[optionsConstant.childOptions.facialExpression.id] = selectedOptions;
          }
        }

        // 13, 데포르메
        if (item.childOptions?.deforme) {
          const selectedOption = Object.values(item.childOptions.deforme).find((option) => option.isSelected);
          if (selectedOption) {
            childOptions[optionsConstant.childOptions.deforme.id] = selectedOption.value;
          }
        }

        // 25, 가로앵글
        if (item.childOptions?.horizontalAngle) {
          const selectedOptions = Object.values(item.childOptions.horizontalAngle)
            .filter((option) => option.isSelected)
            .map((option) => String(option.value));
          if (selectedOptions.length > 0) {
            childOptions[optionsConstant.childOptions.horizontalAngle.id] = selectedOptions;
          }
        }

        // 26, 세로앵글
        if (item.childOptions?.verticalAngle) {
          const selectedOption = Object.values(item.childOptions.verticalAngle).find((option) => option.isSelected);
          if (selectedOption) {
            childOptions[optionsConstant.childOptions.verticalAngle.id] = selectedOption.value;
          }
        }

        // 27, 배경컷
        if (item.childOptions?.backgroundCut) {
          const selectedOption = Object.values(item.childOptions.backgroundCut).find((option) => option.isSelected);
          if (selectedOption) {
            childOptions[optionsConstant.childOptions.backgroundCut.id] = selectedOption.value;
          }
        }

        // 28, 동작구분
        if (item.childOptions?.postureCategory) {
          const selectedOptions = Object.values(item.childOptions.postureCategory)
            .filter((option) => option.isSelected)
            .map((option) => String(option.value));
          if (selectedOptions.length > 0) {
            childOptions[optionsConstant.childOptions.postureCategory.id] = selectedOptions;
          }
        }

        // childOptions가 비어있으면 null로 설정
        if (Object.keys(childOptions).length === 0) {
          childOptions = null;
        }
      } else if (category === CategorySelectEnum.ADULTS_ONLY) {
        childOptions = {};

        // 1, 총 인원수 / 2, 남성 인원수 / 3, 여성 인원수
        if (item.childOptions?.characterNumber) {
          const characterNumber = item.childOptions.characterNumber as {
            maleNumber?: number | string;
            femaleNumber?: number | string;
          };
          if (characterNumber.maleNumber !== undefined && characterNumber.femaleNumber !== undefined) {
            childOptions[optionsConstant.childOptions.wholeGenderNumber.id] =
              typeof characterNumber.maleNumber === 'number' &&
              typeof characterNumber.femaleNumber === 'number' &&
              characterNumber.maleNumber + characterNumber.femaleNumber < 4
                ? characterNumber.maleNumber + characterNumber.femaleNumber
                : '4+';
            childOptions[optionsConstant.childOptions.maleNumber.id] = characterNumber.maleNumber;
            childOptions[optionsConstant.childOptions.femaleNumber.id] = characterNumber.femaleNumber;
          }
        }

        // 4, 샷 구분(프레임)
        childOptions[optionsConstant.childOptions['shot'].id] = Object.values(item.childOptions?.shot ?? {}).find(
          (option) => option.isSelected
        ).value;

        // 16, 여체
        childOptions[optionsConstant.childOptions.femaleBody.id] = Object.values(
          item.childOptions?.femaleBody ?? {}
        ).find((option) => option.isSelected).value;

        // 18, 체위
        childOptions[optionsConstant.childOptions.sexualPosition.id] = selectedSexualPosition;

        // 22, 신체부위
        childOptions[optionsConstant.childOptions.sexualBodyPart.id] = Object.values(
          item.childOptions?.sexualBodyPart ?? {}
        )
          .filter((option) => option.isSelected)
          .map((option) => String(option.value));

        // 25, 가로앵글
        childOptions[optionsConstant.childOptions['horizontalAngle'].id] = Object.values(
          item.childOptions?.horizontalAngle ?? {}
        )
          .filter((option) => option.isSelected)
          .map((option) => String(option.value));

        // 26, 세로앵글
        childOptions[optionsConstant.childOptions['verticalAngle'].id] = Object.values(
          item.childOptions?.verticalAngle ?? {}
        ).find((option) => option.isSelected).value;
      }

      return {
        name: item.name,
        key: item.key ?? '',
        tags: item.tags ?? [],
        order: item.order ?? 0,
        comic: item.comic,
        parentOptions: getParentOptionId(category),
        childOptions
      };
    });

    postImageListMutate(requestBody);
  };

  const executeEditSaveWorkflow = async (imageList: IUploadImageListItem[]) => {
    try {
      // PATCH 이슈 리포트
      const allCheckedIssueReportIds = imageList
        .filter((image) => image.checkedIssueReportIds && image.checkedIssueReportIds.length > 0)
        .flatMap((image) => image.checkedIssueReportIds)
        .filter((id): id is number => id !== undefined);

      if (allCheckedIssueReportIds.length > 0) {
        await sendPatchCheckedIssueReportIds(allCheckedIssueReportIds);
      }

      // PATCH 이미지 정보수정
      const patchImageDataRequestBody = createPatchImageDataRequestBody(imageList);
      if (Object.keys(patchImageDataRequestBody).length > 0) {
        await sendPatchImageData(patchImageDataRequestBody);
      }

      // POST 이미지 삭제
      const deletedImageIds = originalImageList
        .filter(
          (originalItem) => originalItem.imageId && !imageList.some((item) => item.imageId === originalItem.imageId)
        )
        .map((item) => item.imageId!)
        .filter((id): id is number => id !== undefined);

      if (deletedImageIds.length > 0) {
        await sendPostImagesBulkDelete(deletedImageIds);
      }

      // 성공 모달 보여주기
      setModalType(AdminModalType.IMAGE_EDIT_SUCCESS);
      setModalTitle('변경된 내용이 저장되었습니다.');
      setModalContent('리스트로 돌아갑니다.');
      setIsModalVisible(true);
    } catch (error) {
      // 에러는 각 send... 함수 내부에서 이미 처리됨
    }
  };

  const sendPatchCheckedIssueReportIds = async (checkedIssueReportIds: number[]) => {
    const requestBody = [...checkedIssueReportIds];

    try {
      await patchIssueReportListMutateAsync(requestBody);
    } catch (error) {
      setModalType(AdminModalType.IMAGE_UPLOAD_REQUEST_FAIL);
      setModalTitle('업로드에 실패하였습니다.');
      setModalContent('요청이 실패하였습니다. 다시 시도해주세요.');
      setIsModalVisible(true);
      setIsRequestPending(false);
      throw error;
    }
  };

  const createPatchImageDataRequestBody = (imageList: IUploadImageListItem[]) => {
    const requestBody: IPatchImageUploadBody = {};

    // 작품명을 변경한 경우
    if (originalComic.name.trim() !== inputKeyword.trim() && originalComic.id) {
      //requestBody에 프로퍼티 추가
      requestBody.newComicName = {
        id: originalComic.id,
        name: inputKeyword.trim()
      };
    }

    // order가 변경된 이미지
    const moves = imageList
      .filter((item) => 'imageId' in item) // 기존 이미지만 필터링
      .filter((item) => {
        const originalItem = originalImageList.find((original) => original.imageId === item.imageId);
        return originalItem && originalItem.order !== item.order;
      })
      .map((item) => ({
        imageId: item.imageId!,
        name: item.name,
        newOrder: item.order!
      }));

    if (moves.length > 0) {
      requestBody.moves = moves;
    }

    // childOptions와 tags가 변경된 이미지
    const updates = imageList
      .filter((item) => 'imageId' in item) // 기존 이미지만 필터링
      .map((item) => {
        const update: IUpdates = {
          imageId: item.imageId!
        };

        // childOptions 처리
        const changedChildOptions: IChildOptions = {};

        if (category === CategorySelectEnum.STANDARD) {
          // childOptions value 변경 여부 확인
          if (item.childOptions !== null) {
            // 1,2,3 인원수 변경 확인
            const originalCharacterNumber =
              item.originalChildOptions?.characterNumber &&
              typeof item.originalChildOptions.characterNumber === 'object' &&
              'maleNumber' in item.originalChildOptions.characterNumber
                ? {
                    maleNumber: (
                      item.originalChildOptions.characterNumber as {
                        maleNumber?: number | string;
                        femaleNumber?: number | string;
                      }
                    ).maleNumber,
                    femaleNumber: (
                      item.originalChildOptions.characterNumber as {
                        maleNumber?: number | string;
                        femaleNumber?: number | string;
                      }
                    ).femaleNumber
                  }
                : undefined;
            const currentCharacterNumber =
              item.childOptions?.characterNumber &&
              typeof item.childOptions.characterNumber === 'object' &&
              'maleNumber' in item.childOptions.characterNumber
                ? {
                    maleNumber: (
                      item.childOptions.characterNumber as {
                        maleNumber?: number | string;
                        femaleNumber?: number | string;
                      }
                    ).maleNumber,
                    femaleNumber: (
                      item.childOptions.characterNumber as {
                        maleNumber?: number | string;
                        femaleNumber?: number | string;
                      }
                    ).femaleNumber
                  }
                : undefined;
            if (
              (originalCharacterNumber && !currentCharacterNumber) ||
              (!originalCharacterNumber && currentCharacterNumber) ||
              originalCharacterNumber?.maleNumber !== currentCharacterNumber?.maleNumber ||
              originalCharacterNumber?.femaleNumber !== currentCharacterNumber?.femaleNumber
            ) {
              changedChildOptions[optionsConstant.childOptions.wholeGenderNumber.id] =
                typeof currentCharacterNumber?.maleNumber === 'number' &&
                typeof currentCharacterNumber?.femaleNumber === 'number' &&
                currentCharacterNumber?.maleNumber + currentCharacterNumber?.femaleNumber < 4
                  ? currentCharacterNumber?.maleNumber + currentCharacterNumber?.femaleNumber
                  : '4+';
              changedChildOptions[optionsConstant.childOptions.maleNumber.id] = currentCharacterNumber?.maleNumber;
              changedChildOptions[optionsConstant.childOptions.femaleNumber.id] = currentCharacterNumber?.femaleNumber;

              changedChildOptions[optionsConstant.childOptions['maleNumber'].id] = currentCharacterNumber?.maleNumber;

              changedChildOptions[optionsConstant.childOptions['femaleNumber'].id] =
                currentCharacterNumber?.femaleNumber;
            }

            // 4, 샷 구분(프레임) 변경 확인
            const originalShot = Object.values(item.originalChildOptions?.shot ?? {}).find(
              (option) => option.isSelected
            )?.value;
            const currentShot = Object.values(item.childOptions.shot ?? {}).find((option) => option.isSelected)?.value;
            if (originalShot !== currentShot) {
              changedChildOptions[optionsConstant.childOptions['shot'].id] = currentShot;
            }

            // 5, 연출 변경 확인
            const originalDirected = Object.values(item.originalChildOptions?.directed ?? {}).find(
              (option) => option.isSelected
            )?.value;
            const currentDirected = Object.values(item.childOptions.directed ?? {}).find(
              (option) => option.isSelected
            )?.value;
            if (originalDirected !== currentDirected) {
              changedChildOptions[optionsConstant.childOptions['directed'].id] = currentDirected;
            }

            // 6, 연령대 변경 확인
            const originalAgeRange = Object.values(item.originalChildOptions?.ageRange ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            const currentAgeRange = Object.values(item.childOptions.ageRange ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            if (JSON.stringify(originalAgeRange) !== JSON.stringify(currentAgeRange)) {
              changedChildOptions[optionsConstant.childOptions.ageRange.id] = currentAgeRange;
            }

            // 8, 스킨십 변경 확인
            const originalSkinship = Object.values(item.originalChildOptions?.skinship ?? {}).find(
              (option) => option.isSelected
            )?.value;
            const currentSkinship = Object.values(item.childOptions.skinship ?? {}).find(
              (option) => option.isSelected
            )?.value;
            if (originalSkinship !== currentSkinship) {
              changedChildOptions[optionsConstant.childOptions.skinship.id] = currentSkinship;
            }

            // 9, 중심동작부위 변경 확인
            const originalMainMotionPart = Object.values(item.originalChildOptions?.mainMotionPart ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            const currentMainMotionPart = Object.values(item.childOptions.mainMotionPart ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            if (JSON.stringify(originalMainMotionPart) !== JSON.stringify(currentMainMotionPart)) {
              changedChildOptions[optionsConstant.childOptions.mainMotionPart.id] = currentMainMotionPart;
            }

            // 10, 표정 변경 확인
            const originalFacialExpression = Object.values(item.originalChildOptions?.facialExpression ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            const currentFacialExpression = Object.values(item.childOptions.facialExpression ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            if (JSON.stringify(originalFacialExpression) !== JSON.stringify(currentFacialExpression)) {
              changedChildOptions[optionsConstant.childOptions.facialExpression.id] = currentFacialExpression;
            }

            // 13, 데포르메 변경 확인
            const originalDeforme = Object.values(item.originalChildOptions?.deforme ?? {}).find(
              (option) => option.isSelected
            )?.value;
            const currentDeforme = Object.values(item.childOptions.deforme ?? {}).find(
              (option) => option.isSelected
            )?.value;
            if (originalDeforme !== currentDeforme) {
              changedChildOptions[optionsConstant.childOptions.deforme.id] = currentDeforme;
            }

            // 25, 가로앵글 변경 확인
            const originalHorizontalAngle = Object.values(item.originalChildOptions?.horizontalAngle ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            const currentHorizontalAngle = Object.values(item.childOptions.horizontalAngle ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            if (JSON.stringify(originalHorizontalAngle) !== JSON.stringify(currentHorizontalAngle)) {
              changedChildOptions[optionsConstant.childOptions.horizontalAngle.id] = currentHorizontalAngle;
            }

            // 26, 세로앵글 변경 확인
            const originalVerticalAngle = Object.values(item.originalChildOptions?.verticalAngle ?? {}).find(
              (option) => option.isSelected
            )?.value;
            const currentVerticalAngle = Object.values(item.childOptions.verticalAngle ?? {}).find(
              (option) => option.isSelected
            )?.value;
            if (originalVerticalAngle !== currentVerticalAngle) {
              changedChildOptions[optionsConstant.childOptions['verticalAngle'].id] = currentVerticalAngle;
            }

            // 27, 배경컷 변경 확인
            const originalBackgroundCut = Object.values(item.originalChildOptions?.directed ?? {}).find(
              (option) => option.isSelected
            )?.value;
            const currentBackgroundCut = Object.values(item.childOptions.directed ?? {}).find(
              (option) => option.isSelected
            )?.value;
            if (originalBackgroundCut !== currentBackgroundCut) {
              changedChildOptions[optionsConstant.childOptions['backgroundCut'].id] = currentBackgroundCut;
            }

            // 28, 동작구분 변경 확인
            const originalPostureCategory = Object.values(item.originalChildOptions?.postureCategory ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            const currentPostureCategory = Object.values(item.childOptions.postureCategory ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            if (JSON.stringify(originalPostureCategory) !== JSON.stringify(currentPostureCategory)) {
              changedChildOptions[optionsConstant.childOptions.postureCategory.id] = currentPostureCategory;
            }
          }

          // 변경된 childOptions가 있는 경우에만 추가, 없으면 undefined
          if (Object.keys(changedChildOptions).length > 0) {
            const childOptions: IChildOptions = {};

            // 1, 총 인원수 / 2, 남성 인원수 / 3, 여성 인원수
            if (item.childOptions?.characterNumber) {
              const characterNumber = item.childOptions.characterNumber as {
                maleNumber?: number | string;
                femaleNumber?: number | string;
              };
              if (characterNumber.maleNumber !== undefined && characterNumber.femaleNumber !== undefined) {
                childOptions[optionsConstant.childOptions.wholeGenderNumber.id] =
                  typeof characterNumber.maleNumber === 'number' &&
                  typeof characterNumber.femaleNumber === 'number' &&
                  characterNumber.maleNumber + characterNumber.femaleNumber < 4
                    ? characterNumber.maleNumber + characterNumber.femaleNumber
                    : '4+';
                childOptions[optionsConstant.childOptions.maleNumber.id] = characterNumber.maleNumber;
                childOptions[optionsConstant.childOptions.femaleNumber.id] = characterNumber.femaleNumber;
              }
            }

            // 4, 샷 구분(프레임)
            if (item.childOptions?.shot) {
              const selectedOption = Object.values(item.childOptions.shot).find((option) => option.isSelected);
              if (selectedOption) {
                childOptions[optionsConstant.childOptions.shot.id] = selectedOption.value;
              }
            }

            // 5, 연출
            if (item.childOptions?.directed) {
              const selectedOption = Object.values(item.childOptions.directed).find((option) => option.isSelected);
              if (selectedOption) {
                childOptions[optionsConstant.childOptions.directed.id] = selectedOption.value;
              }
            }

            // 6, 연령대
            if (item.childOptions?.ageRange) {
              const selectedOptions = Object.values(item.childOptions.ageRange)
                .filter((option) => option.isSelected)
                .map((option) => String(option.value));
              if (selectedOptions.length > 0) {
                childOptions[optionsConstant.childOptions.ageRange.id] = selectedOptions;
              }
            }

            // 8, 스킨십
            if (item.childOptions?.skinship) {
              const selectedOption = Object.values(item.childOptions.skinship).find((option) => option.isSelected);
              if (selectedOption) {
                childOptions[optionsConstant.childOptions.skinship.id] = selectedOption.value;
              }
            }

            // 9, 중심동작부위
            if (item.childOptions?.mainMotionPart) {
              const selectedOptions = Object.values(item.childOptions.mainMotionPart)
                .filter((option) => option.isSelected)
                .map((option) => String(option.value));
              if (selectedOptions.length > 0) {
                childOptions[optionsConstant.childOptions.mainMotionPart.id] = selectedOptions;
              }
            }

            // 10, 표정
            if (item.childOptions?.facialExpression) {
              const selectedOptions = Object.values(item.childOptions.facialExpression)
                .filter((option) => option.isSelected)
                .map((option) => String(option.value));
              if (selectedOptions.length > 0) {
                childOptions[optionsConstant.childOptions.facialExpression.id] = selectedOptions;
              }
            }

            // 13, 데포르메
            if (item.childOptions?.deforme) {
              const selectedOption = Object.values(item.childOptions.deforme).find((option) => option.isSelected);
              if (selectedOption) {
                childOptions[optionsConstant.childOptions.deforme.id] = selectedOption.value;
              }
            }

            // 25, 가로앵글
            if (item.childOptions?.horizontalAngle) {
              const selectedOptions = Object.values(item.childOptions.horizontalAngle)
                .filter((option) => option.isSelected)
                .map((option) => String(option.value));
              if (selectedOptions.length > 0) {
                childOptions[optionsConstant.childOptions.horizontalAngle.id] = selectedOptions;
              }
            }

            // 26, 세로앵글
            if (item.childOptions?.verticalAngle) {
              const selectedOption = Object.values(item.childOptions.verticalAngle).find((option) => option.isSelected);
              if (selectedOption) {
                childOptions[optionsConstant.childOptions.verticalAngle.id] = selectedOption.value;
              }
            }

            // 27, 배경컷
            if (item.childOptions?.backgroundCut) {
              const selectedOption = Object.values(item.childOptions.backgroundCut).find((option) => option.isSelected);
              if (selectedOption) {
                childOptions[optionsConstant.childOptions.backgroundCut.id] = selectedOption.value;
              }
            }

            // 28, 동작구분
            if (item.childOptions?.postureCategory) {
              const selectedOptions = Object.values(item.childOptions.postureCategory)
                .filter((option) => option.isSelected)
                .map((option) => String(option.value));
              if (selectedOptions.length > 0) {
                childOptions[optionsConstant.childOptions.postureCategory.id] = selectedOptions;
              }
            }

            update.childOptions = childOptions;
          } else {
            update.childOptions = undefined;
          }
        } else if (category === CategorySelectEnum.ADULTS_ONLY) {
          // childOptions value 변경 여부 확인
          if (item.childOptions !== null) {
            // 1,2,3 인원수 변경 확인
            const originalCharacterNumber = item.originalChildOptions?.characterNumber as {
              maleNumber?: number | string;
              femaleNumber?: number | string;
            };
            const currentCharacterNumber = item.childOptions.characterNumber as {
              maleNumber?: number | string;
              femaleNumber?: number | string;
            };
            if (
              originalCharacterNumber?.maleNumber !== currentCharacterNumber?.maleNumber ||
              originalCharacterNumber?.femaleNumber !== currentCharacterNumber?.femaleNumber
            ) {
              changedChildOptions[optionsConstant.childOptions.wholeGenderNumber.id] =
                typeof currentCharacterNumber?.maleNumber === 'number' &&
                typeof currentCharacterNumber?.femaleNumber === 'number' &&
                currentCharacterNumber?.maleNumber + currentCharacterNumber?.femaleNumber < 4
                  ? currentCharacterNumber?.maleNumber + currentCharacterNumber?.femaleNumber
                  : '4+';

              changedChildOptions[optionsConstant.childOptions['maleNumber'].id] = currentCharacterNumber?.maleNumber;

              changedChildOptions[optionsConstant.childOptions['femaleNumber'].id] =
                currentCharacterNumber?.femaleNumber;
            }

            // 4, 샷 구분(프레임) 변경 확인
            const originalShot = Object.values(item.originalChildOptions?.shot ?? {}).find(
              (option) => option.isSelected
            )?.value;
            const currentShot = Object.values(item.childOptions.shot ?? {}).find((option) => option.isSelected)?.value;
            if (originalShot !== currentShot) {
              changedChildOptions[optionsConstant.childOptions['shot'].id] = currentShot;
            }

            // 16, 여체 변경 확인
            const originalFemaleBody = Object.values(item.originalChildOptions?.femaleBody ?? {}).find(
              (option) => option.isSelected
            )?.value;
            const currentFemaleBody = Object.values(item.childOptions.femaleBody ?? {}).find(
              (option) => option.isSelected
            )?.value;
            if (originalFemaleBody !== currentFemaleBody) {
              changedChildOptions[optionsConstant.childOptions.femaleBody.id] = currentFemaleBody;
            }

            // 22, 신체부위 변경 확인
            const originalSexualBodyPart = Object.values(item.originalChildOptions?.sexualBodyPart ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            const currentSexualBodyPart = Object.values(item.childOptions.sexualBodyPart ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            if (JSON.stringify(originalSexualBodyPart) !== JSON.stringify(currentSexualBodyPart)) {
              changedChildOptions[optionsConstant.childOptions.sexualBodyPart.id] = currentSexualBodyPart;
            }

            // 25, 가로앵글 변경 확인
            const originalHorizontalAngle = Object.values(item.originalChildOptions?.horizontalAngle ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            const currentHorizontalAngle = Object.values(item.childOptions.horizontalAngle ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value))
              .sort();
            if (JSON.stringify(originalHorizontalAngle) !== JSON.stringify(currentHorizontalAngle)) {
              changedChildOptions[optionsConstant.childOptions.horizontalAngle.id] = currentHorizontalAngle;
            }

            // 26, 세로앵글 변경 확인
            const originalVerticalAngle = Object.values(item.originalChildOptions?.verticalAngle ?? {}).find(
              (option) => option.isSelected
            )?.value;
            const currentVerticalAngle = Object.values(item.childOptions.verticalAngle ?? {}).find(
              (option) => option.isSelected
            )?.value;
            if (originalVerticalAngle !== currentVerticalAngle) {
              changedChildOptions[optionsConstant.childOptions['verticalAngle'].id] = currentVerticalAngle;
            }
          }
          // 18, 체위 변경 확인
          // 체위의 경우 item.childOptions가 null이더라도 originalSelectedSexualPosition과 selectedSexualPosition값이 다르다면 updates 배열에 실려야 한다.
          if (originalSelectedSexualPosition !== selectedSexualPosition) {
            changedChildOptions[optionsConstant.childOptions.sexualPosition.id] = selectedSexualPosition;
          }

          // 변경된 childOptions가 있는 경우에만 추가, 없으면 undefined
          if (Object.keys(changedChildOptions).length > 0) {
            const childOptions: IChildOptions = {};

            // 1, 총 인원수 / 2, 남성 인원수 / 3, 여성 인원수
            if (item.childOptions?.characterNumber) {
              const characterNumber = item.childOptions.characterNumber as {
                maleNumber?: number | string;
                femaleNumber?: number | string;
              };
              if (characterNumber.maleNumber !== undefined && characterNumber.femaleNumber !== undefined) {
                childOptions[optionsConstant.childOptions.wholeGenderNumber.id] =
                  typeof characterNumber.maleNumber === 'number' &&
                  typeof characterNumber.femaleNumber === 'number' &&
                  characterNumber.maleNumber + characterNumber.femaleNumber < 4
                    ? characterNumber.maleNumber + characterNumber.femaleNumber
                    : '4+';
                childOptions[optionsConstant.childOptions.maleNumber.id] = characterNumber.maleNumber;
                childOptions[optionsConstant.childOptions.femaleNumber.id] = characterNumber.femaleNumber;
              }
            }

            // 4, 샷 구분(프레임)
            childOptions[optionsConstant.childOptions['shot'].id] = Object.values(item.childOptions?.shot ?? {}).find(
              (option) => option.isSelected
            ).value;

            // 16, 여체
            childOptions[optionsConstant.childOptions.femaleBody.id] = Object.values(
              item.childOptions?.femaleBody ?? {}
            ).find((option) => option.isSelected).value;

            // 18, 체위
            childOptions[optionsConstant.childOptions.sexualPosition.id] = selectedSexualPosition;

            // 22, 신체부위
            childOptions[optionsConstant.childOptions.sexualBodyPart.id] = Object.values(
              item.childOptions?.sexualBodyPart ?? {}
            )
              .filter((option) => option.isSelected)
              .map((option) => String(option.value));

            // 25, 가로앵글
            childOptions[optionsConstant.childOptions['horizontalAngle'].id] = Object.values(
              item.childOptions?.horizontalAngle ?? {}
            )
              .filter((option) => option.isSelected)
              .map((option) => String(option.value));

            // 26, 세로앵글
            childOptions[optionsConstant.childOptions['verticalAngle'].id] = Object.values(
              item.childOptions?.verticalAngle ?? {}
            ).find((option) => option.isSelected).value;

            update.childOptions = childOptions;
          } else {
            update.childOptions = undefined;
          }
        }

        // tags 처리
        if (item.tags !== null) {
          const originalTags = item.originalTags?.sort() ?? [];
          const currentTags = item.tags.sort();
          if (JSON.stringify(originalTags) !== JSON.stringify(currentTags)) {
            update.tags = currentTags;
          }
        }

        return update;
      })
      .filter((update) => update.childOptions !== undefined || update.tags !== undefined); // 변경사항이 있는 항목만 필터링

    if (updates.length > 0) {
      requestBody.updates = updates;
    }

    // 새로 첨부된 이미지
    const addImages = imageList
      .filter((item) => !('imageId' in item))
      .map((item) => {
        const addImage: IAddImages = {
          name: item.name,
          key: item.key!,
          order: item.order!,
          tags: item.tags ?? [],
          comic: { name: inputKeyword.trim() },
          sequence: sequenceId ? { id: sequenceId } : null,
          childOptions: {} // 하단에서 프로퍼티 추가
        };

        if (category === CategorySelectEnum.STANDARD) {
          const childOptions: IChildOptions = {};

          // 1, 총 인원수 / 2, 남성 인원수 / 3, 여성 인원수
          if (item.childOptions?.characterNumber) {
            const characterNumber = item.childOptions.characterNumber as {
              maleNumber?: number;
              femaleNumber?: number;
            };
            if (characterNumber.maleNumber !== undefined && characterNumber.femaleNumber !== undefined) {
              childOptions[optionsConstant.childOptions.wholeGenderNumber.id] =
                characterNumber.maleNumber + characterNumber.femaleNumber;
              childOptions[optionsConstant.childOptions.maleNumber.id] = characterNumber.maleNumber;
              childOptions[optionsConstant.childOptions.femaleNumber.id] = characterNumber.femaleNumber;
            }
          }

          // 4, 샷 구분(프레임)
          if (item.childOptions?.shot) {
            const selectedOption = Object.values(item.childOptions.shot).find((option) => option.isSelected);
            if (selectedOption) {
              childOptions[optionsConstant.childOptions.shot.id] = selectedOption.value;
            }
          }

          // 5, 연출
          if (item.childOptions?.directed) {
            const selectedOption = Object.values(item.childOptions.directed).find((option) => option.isSelected);
            if (selectedOption) {
              childOptions[optionsConstant.childOptions.directed.id] = selectedOption.value;
            }
          }

          // 6, 연령대
          if (item.childOptions?.ageRange) {
            const selectedOptions = Object.values(item.childOptions.ageRange)
              .filter((option) => option.isSelected)
              .map((option) => String(option.value));
            if (selectedOptions.length > 0) {
              childOptions[optionsConstant.childOptions.ageRange.id] = selectedOptions;
            }
          }

          // 8, 스킨십
          if (item.childOptions?.skinship) {
            const selectedOption = Object.values(item.childOptions.skinship).find((option) => option.isSelected);
            if (selectedOption) {
              childOptions[optionsConstant.childOptions.skinship.id] = selectedOption.value;
            }
          }

          // 9, 중심동작부위
          if (item.childOptions?.mainMotionPart) {
            const selectedOptions = Object.values(item.childOptions.mainMotionPart)
              .filter((option) => option.isSelected)
              .map((option) => String(option.value));
            if (selectedOptions.length > 0) {
              childOptions[optionsConstant.childOptions.mainMotionPart.id] = selectedOptions;
            }
          }

          // 10, 표정
          if (item.childOptions?.facialExpression) {
            const selectedOptions = Object.values(item.childOptions.facialExpression)
              .filter((option) => option.isSelected)
              .map((option) => String(option.value));
            if (selectedOptions.length > 0) {
              childOptions[optionsConstant.childOptions.facialExpression.id] = selectedOptions;
            }
          }

          // 13, 데포르메
          if (item.childOptions?.deforme) {
            const selectedOption = Object.values(item.childOptions.deforme).find((option) => option.isSelected);
            if (selectedOption) {
              childOptions[optionsConstant.childOptions.deforme.id] = selectedOption.value;
            }
          }

          // 25, 가로앵글
          if (item.childOptions?.horizontalAngle) {
            const selectedOptions = Object.values(item.childOptions.horizontalAngle)
              .filter((option) => option.isSelected)
              .map((option) => String(option.value));
            if (selectedOptions.length > 0) {
              childOptions[optionsConstant.childOptions.horizontalAngle.id] = selectedOptions;
            }
          }

          // 26, 세로앵글
          if (item.childOptions?.verticalAngle) {
            const selectedOption = Object.values(item.childOptions.verticalAngle).find((option) => option.isSelected);
            if (selectedOption) {
              childOptions[optionsConstant.childOptions.verticalAngle.id] = selectedOption.value;
            }
          }

          // 27, 배경컷
          if (item.childOptions?.backgroundCut) {
            const selectedOption = Object.values(item.childOptions.backgroundCut).find((option) => option.isSelected);
            if (selectedOption) {
              childOptions[optionsConstant.childOptions.backgroundCut.id] = selectedOption.value;
            }
          }

          // 28, 동작구분
          if (item.childOptions?.postureCategory) {
            const selectedOptions = Object.values(item.childOptions.postureCategory)
              .filter((option) => option.isSelected)
              .map((option) => String(option.value));
            if (selectedOptions.length > 0) {
              childOptions[optionsConstant.childOptions.postureCategory.id] = selectedOptions;
            }
          }

          addImage.childOptions = childOptions;
        } else if (category === CategorySelectEnum.ADULTS_ONLY) {
          const childOptions: IChildOptions = {
            // 1, 총 인원수
            [optionsConstant.childOptions['wholeGenderNumber'].id]: Number(
              ((item.childOptions?.characterNumber as { maleNumber?: number })?.maleNumber ?? 0) +
                ((item.childOptions?.characterNumber as { femaleNumber?: number })?.femaleNumber ?? 0)
            ),
            // 2, 남성 인원수
            [optionsConstant.childOptions['maleNumber'].id]: Number(
              (item.childOptions?.characterNumber as { maleNumber?: number })?.maleNumber ?? 0
            ),
            // 3, 여성 인원수
            [optionsConstant.childOptions['femaleNumber'].id]: Number(
              (item.childOptions?.characterNumber as { femaleNumber?: number })?.femaleNumber ?? 0
            ),
            // 4, 샷 구분(프레임)
            [optionsConstant.childOptions['shot'].id]: Object.values(item.childOptions?.shot ?? {}).find(
              (option) => option.isSelected
            ).value,
            // 16, 여체
            [optionsConstant.childOptions.femaleBody.id]: Object.values(item.childOptions?.femaleBody ?? {}).find(
              (option) => option.isSelected
            ).value,
            // 18, 체위
            [optionsConstant.childOptions.sexualPosition.id]: selectedSexualPosition,
            // 22, 신체부위
            [optionsConstant.childOptions.sexualBodyPart.id]: Object.values(item.childOptions?.sexualBodyPart ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value)),
            // 25, 가로앵글
            [optionsConstant.childOptions['horizontalAngle'].id]: Object.values(
              item.childOptions?.horizontalAngle ?? {}
            )
              .filter((option) => option.isSelected)
              .map((option) => String(option.value)),
            // 26, 세로앵글
            [optionsConstant.childOptions['verticalAngle'].id]: Object.values(
              item.childOptions?.verticalAngle ?? {}
            ).find((option) => option.isSelected).value
          };

          addImage.childOptions = childOptions;
        }

        return addImage;
      });

    if (addImages.length > 0) {
      requestBody.addImages = addImages;
    }

    return requestBody;
  };

  const sendPatchImageData = async (requestBody: IPatchImageUploadBody) => {
    try {
      await patchImageDataMutateAsync(requestBody);
    } catch (error) {
      setModalType(AdminModalType.IMAGE_UPLOAD_REQUEST_FAIL);
      setModalTitle('업로드에 실패하였습니다.');
      setModalContent('요청이 실패하였습니다. 다시 시도해주세요.');
      setIsModalVisible(true);
      setIsRequestPending(false);
      throw error;
    }
  };

  const sendPostImagesBulkDelete = async (deletedImageIds: number[]) => {
    try {
      await postImagesBulkDeleteMutateAsync({ imageId: deletedImageIds });
    } catch (error) {
      setModalType(AdminModalType.IMAGE_UPLOAD_REQUEST_FAIL);
      setModalTitle('업로드에 실패하였습니다.');
      setModalContent('요청이 실패하였습니다. 다시 시도해주세요.');
      setIsModalVisible(true);
      setIsRequestPending(false);
      throw error;
    }
  };

  const handleModalConfirmClick = () => {
    if (modalType === AdminModalType.IMAGE_UPLOAD_CANCEL || modalType === AdminModalType.IMAGE_EDIT_CANCEL) {
      navigate(-1); // 브라우저 히스토리 한 단계 뒤로
    } else if (modalType === AdminModalType.IMAGE_DELETE_CONFIRM) {
      if (!focusedImage) return;

      // 이미지 삭제
      const filtered = imageList
        .filter((item) => item !== focusedImage)
        .map((item) => ({
          ...item,
          type: (item as IUploadImageListItem).type || item.name?.split('.').pop() || 'jpg'
        }));

      const newImageList = filtered.map((item, idx) => ({
        ...item,
        order: idx + 1,
        name: createImageName(
          inputKeyword,
          item.imageId ? item.imageId : lastImageId + 1,
          filtered.length,
          idx,
          item.type,
          sequenceId ? sequenceId : lastSequenceId + 1,
          item.file,
          item.originalFileName
        )
      }));

      setImageList(newImageList);

      const targetIndex = focusedImage.order && focusedImage.order <= newImageList.length ? focusedImage.order - 1 : 0;
      const targetImage = newImageList[targetIndex]; // 삭제된 이미지의 다음 이미지. 다음 이미지가 없을 경우 0번 index 이미지

      // 포커스 이동
      // newImageList의 length가 1 이상이므로 targetImage가 존재할 경우에만 실행
      if (targetImage) {
        // newImageList를 사용하여 handleImageFocus 로직 직접 실행
        setFocusedImageIndex(targetIndex);

        setImageList((prev) => {
          return prev.map((item, idx) => ({
            ...item,
            isFocus: idx === targetIndex
          }));
        });

        invokeImageApiCall(newImageList[targetIndex], targetIndex);
      }

      setIsModalVisible(false);
      showToast(ToastMessageEnum.IMAGE_DELETE_SUCCESS);
    } else if (modalType === AdminModalType.IMAGE_UPLOAD_SUCCESS) {
      setIsModalVisible(false);
      //분류에 따라 스탠다드 이미지 리스트 페이지 또는 19금 이미지 리스트 페이지로 이동
      if (category === CategorySelectEnum.STANDARD) {
        navigate(PathnameEnum.ADMIN_STANDARD_IMAGE_LIST_PAGE);
      } else {
        navigate(PathnameEnum.ADMIN_ADULTS_ONLY_IMAGE_LIST_PAGE);
      }
    } else if (modalType === AdminModalType.IMAGE_EDIT_SUCCESS) {
      setIsModalVisible(false);
      navigate(-1); // 브라우저 히스토리 한 단계 뒤로
    } else if (modalType === AdminModalType.OMIT_REQUIRED_INPUT) {
      setIsModalVisible(false);
      setIsSaveAttempted(true); // 저장 시도 플래그 ON. 모달 닫은 후 error UI 노출
    }
  };

  // isCreate와 이미지 갯수에 따른 저장 버튼 분기
  useEffect(() => {
    if (isCreate && imageList.length === 0) {
      setNeedImageAttach(true);
    } else {
      setNeedImageAttach(false);
    }
  }, [imageList]);

  // 분류값 변경시 0번 자동 focus 및 AI 추천값 불러오기 invoke
  useEffect(() => {
    if (category !== prevCategory && imageList.length > 0) {
      setPrevCategory(category);
      handleImageFocus(0);
    }
  }, [category, prevCategory, imageList]);

  // 이미지 업로드 페이지 첫 이미지 업로드시 0번 자동 focus
  useEffect(() => {
    if (isCreate && prevImageListLength === 0 && imageList.length > 0) {
      handleImageFocus(0);
    }
    setPrevImageListLength(imageList.length);
  }, [imageList.length, isCreate]);

  //GET /admin/images/${imageId} response.data를 setImageList
  useEffect(() => {
    if (!isCreate && fetchedImageData) {
      // 작품명 setState
      if (fetchedImageData.comic && fetchedImageData.comic.name) {
        setOriginalComic(fetchedImageData.comic);
        setInputKeyword(fetchedImageData.comic.name);
      }

      const newImageList = fetchedImageData.images.map((image, index) => {
        const ext = image.s3Url?.split('.').pop() ?? 'jpg';

        return {
          name: createImageName(
            fetchedImageData.comic?.name ?? '',
            image.id,
            fetchedImageData.images.length,
            index,
            `image/${ext}`,
            image.sequence?.id
          ),
          url: image.s3Url,
          comic: {
            id: fetchedImageData.comic?.id,
            name: fetchedImageData.comic?.name ?? ''
          },
          isFocus: false,
          order: fetchedImageData.images.length === 1 ? undefined : index + 1, // 단일컷인 경우 order가 undefined
          childOptions: null,
          tags: null,
          type: `image/${ext}`,
          imageId: image.id,
          uniqueFileName: image.uniqueFileName,
          originalFileName: createImageName(
            fetchedImageData.comic?.name ?? '',
            image.id,
            fetchedImageData.images.length,
            index,
            `image/${ext}`,
            image.sequence?.id
          )
        };
      });

      setOriginalImageList(newImageList);
      setImageList(newImageList);

      // 이미지 수정 페이지에서 GET /admin/images/${imageId} fetch 직후 focusedImage의 정보를 불러오기 위함
      const targetIndex = fetchedImageData.images.findIndex((image) => image.id === Number(imageId));
      if (targetIndex !== -1) {
        // newImageList를 사용하여 handleImageFocus 로직 직접 실행
        setFocusedImageIndex(targetIndex);

        setImageList((prev) => {
          return prev.map((item, idx) => ({
            ...item,
            isFocus: idx === targetIndex
          }));
        });

        const targetImage = newImageList[targetIndex];
        invokeImageApiCall(targetImage, targetIndex);
      }
    }
  }, [fetchedImageData]);

  // 키보드 좌우키 누를 시 크게보기 컷 변경
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // 이미지가 없거나 포커스된 이미지가 없는 경우 무시
      if (!imageList || imageList.length === 0 || focusedImageIndex === null) {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          // 왼쪽 화살표 키: 이전 이미지로 이동
          if (focusedImageIndex > 0) {
            handleImageFocus(focusedImageIndex - 1);
          }
          break;
        case 'ArrowRight':
          // 오른쪽 화살표 키: 다음 이미지로 이동
          if (focusedImageIndex < imageList.length - 1) {
            handleImageFocus(focusedImageIndex + 1);
          }
          break;
        default:
          break;
      }
    };

    // 이벤트 리스너 등록
    window.addEventListener('keydown', handleKeyDown);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [imageList, focusedImageIndex, handleImageFocus]);

  return {
    // 브레드크럼 클릭시
    getBreadcrumbInfo,
    handleBreadCrumbClick,

    // 페이지명 분기
    isCreate,

    // 페이지 취소 버튼
    handlePageCancelClick,

    // 페이지 저장 버튼 관련
    needImageAttach,
    isRequestPending,
    validateImageChildOptions,
    handleSaveButtonClick,

    // 상단 필터 (분류, 장르, 하위 스페셜 단추, 작품명)
    category,
    handleCategoryChange,
    selectedSexualPosition,
    handleSelectedSexualPositionChange,
    inputKeyword,
    autocompleteList,
    handleInputKeywordChange,
    handleAutocompleteChange,
    handleComicNameUpdate,

    // 이미지 업로드 페이지: 하단 이미지 업로드 관련
    imageList,
    handleImageUploadButtonClick,
    handleAttachDrop,
    handleAttachDragOver,
    handleAttachDragEnter,
    handleDragStart,
    handleDragOver,
    handleDragDrop,

    // 우측 이미지 정보란 관련
    focusedImage,
    focusedImageIndex,
    handleImageFocus,
    handleImageDelete,
    getImageIssueReportList,
    updateCheckedIssueReportIds,
    updateFocusedImageChildOptions,
    updateDependentChildOptions,
    isFetchImageAIRecommendationLoading,
    tagInputKeyword,
    tagAutocompleteList,
    handleTagKeyDown,
    handleTagInputKeywordChange,
    handleTagAutocompleteChange,
    addFocusedImageTag,
    deleteFocusedImageTag,

    //에러 관련
    modalType,
    isModalVisible,
    setIsModalVisible,
    modalTitle,
    modalContent,
    handleModalConfirmClick,
    isSaveAttempted,

    // 이미지 수정 페이지 관련
    fetchedImageData
  };
};
